import { structureWallsForRoomWalls } from './floorplanDataView';
import {
  grabHandleOfferedFor,
  moveHandleOfferedFor,
  runPointerTarget,
} from './runPointerTargets';
import { wallLengthDelta } from '../../utils/wallLengthDrag';
import {
  WALL_BODY_DRAG_ENABLED,
  WALL_ENDPOINT_DRAG_ENABLED,
} from '../../utils/floorplanEditCapabilities';
import { useCallback, useEffect, useRef } from 'react';
import {
  apply3DRotationTo2D,
  isPointInSinkBounds,
  isPointNearWall,
  screenToWorldCoordinates,
  worldToScreenCoordinates,
} from '../../utils/geometryUtils';
import {
  calculateResize,
  getHandleCursor,
  hitTestHandles,
  isPointInObject,
} from '../../utils/resizeHandlesUtils';
import {
  getWallEndPoints,
  getWallHandleCursor,
  hitTestWallHandles,
} from '../../utils/wallHandlesUtils';
import { canHaveCountertopOnTop } from '../../utils/objectCategoryHelper';
import { checkPlacement, snapToValidPosition } from '@shared-lib/collision';
import { computeClickedRoomWallSpan } from '../../utils/wholeWallDemo';
import { createSinkFaucet } from '@shared-lib/vanity-assembly/createSinkFaucet';
import { resolveCounterContainer } from '@shared-lib/vanity-assembly/counterContainer';
import { isLowerCabinetLike } from '@shared-lib/group-customization';
import {
  createSinkObject,
  getSinkType,
  getSinksOnCountertop,
  isSink,
  validateSinkPlacement,
} from '../../utils/sinkHelpers';
import { detectClickedElement } from '../../utils/interactionUtils';
import { filterCollisions, toSkipChecks } from '../../utils/constraintFilter';
import { findFloorY } from '../../utils/floorPlanUtils';
import {
  WALL_THICKNESS,
  findRoomContainingPoint,
  getCeilingHeightAtPoint,
} from '../../utils/wallCreationUtils';
import { getDefaultGLBModel } from '../../services/glbModelService';
import { getFreeMoveGroup } from '../../utils/freeMoveGroup';
import { alongWallCursor } from '../../utils/alongWallCursor';
import { hitTestMeasurement } from '../../utils/measurementUtilsV2';
import {
  isDemoedOpening,
  isWallMountedOpening,
} from '../../utils/parentWallHelpers';
import { hitTestGrabHandle, roomCentroid } from '../../utils/grabHandleUtils';
import { fixedPinAt } from '../../utils/fixedCabinetMarker';
import { toDragSpace } from '../../utils/touchDragSpace';
import { isObjectType } from '../../utils/objectTypeUtils';
import {
  isPolygonWall,
  scalePolygonCorners,
} from '../../utils/polygonWallUtils';
import { snapElementToWall } from '../../utils/wallSnappingUtils';
import {
  supportSurfaceAt,
  countertopAtPoint,
} from '../../utils/supportSurface';
import {
  collectAllObjects,
  wireShapeFromHandle,
  WIRE_HANDLE_RADIUS_PX,
} from '../../utils/electricalDrawingUtils';
import {
  isCeilingElectricalDevice,
  isSurfaceElectricalDevice,
  isWallElectricalDevice,
  wallsForHostRoom,
  faceWallDeviceIntoRoom,
  roomForSnappedWall,
} from '../../utils/electricalDevices';
import { pickElectricalTargetAt } from '../../utils/electricalDrawingUtils';
import { runAtPoint } from '../../utils/electricalRuns';
import { calloutAtPoint } from '../../utils/calloutPlan';
import { snapWallEndpointToWall } from '../../utils/snapToWall';
import ParentChildSystem from '../../utils/parentChildSystem';

/**
 * How far a press on a bay EDGE handle must travel before it resizes anything.
 *
 * The handle arms on mousedown, so without this a click that wobbled two
 * pixels is a drag — and where the wobble crosses one of the family's width
 * rungs it commits a real resize from what the user experienced as a click.
 * The edge handles are what made that reachable: before them, clicking a
 * cabinet could not resize it at all.
 *
 * 5 px because that is what the rest of the app already uses for "this was a
 * drag, not a click" — `resizeHandles3D`, `navigationAndEditHandlers` (twice),
 * `RenderWallElevation` (twice) and `useElevationsGestures` all pick 5, so a
 * new number here would make the floor plan feel different from every other
 * surface for no reason. (`TOUCH_DRAG_THRESHOLD` is 10 because a finger is not
 * a mouse; `GroupIsolationView` is the one 3 px outlier.) Those are eight
 * separate copies of one idea and ought to be a shared constant — see
 * `reference-docs/cabinet-runs/overview.md`.
 *
 * Enforced in `useRunDrag`'s commit rather than here, so the mouse and touch
 * paths pass through ONE rule instead of each keeping their own.
 */
const BAY_WIDTH_DRAG_THRESHOLD_PX = 5;

// Floorplan gesture handlers extracted verbatim from UVFloorPlanCanvas.js.
// Handler bodies + effect dependency arrays are byte-identical to the
// original; only the wrapping (ctx destructure + hook signature + the
// returned handler list) is new. selectedAtMouseDownRef stays in the
// coordinator (drawFloorPlan reads it) and is threaded in via ctx.
export function useFloorplanGestures(ctx) {
  const {
    DEFAULT_SCALE,
    activeResizeHandle,
    activeWallHandle,
    autoPanState,
    boundaryAxis,
    canvasRef,
    canvasResizeHandlesFor,
    collisionDebugRef,
    connectionMode,
    containerRef,
    dataSource,
    dragRedirectTargetRef,
    dragStartRef,
    getDataToRender,
    getDefaultSize,
    getElementsWithRoomContext,
    hasDraggedMouse,
    onWallLengthDrag,
    onWallLengthDragStart,
    onWallLengthDragEnd,
    isDraggingObject,
    isDraggingRef,
    isDraggingWall,
    isDraggingWallElement,
    isResizing,
    isWall,
    lastCollisionDebugRef,
    lastDraggedObjectRef,
    measurementsRef,
    mouseMovementRef,
    objectDragStart,
    objectInteractionMode,
    // Which cabinet panel is open — 'bay' or 'run'. The run's handles and a
    // bay's belong to different scopes and are never both on screen, so the
    // hit-tests ask this too (`hooks/useCabinetEditScope`).
    cabinetEditScope = 'bay',
    grabHandlesEnabled = false,
    setDragPointer,
    onConnectionSelect,
    onWiringRunSelect,
    onWireShapeDrag,
    onWireShapeCommit,
    selectedWiringRunId,
    wiringHitRef,
    calloutDrawing,
    calloutHitRef,
    // The pins drawn on fixed cabinets, and what a click on one does.
    fixedPinHitRef,
    onUnpinCabinet,
    onCalloutSelect,
    selectedCalloutId,
    onItemSelect,
    onMeasurementSelect,
    onNotification,
    onPanChange,
    onPlaceObject,
    onSetStretchBoundary,
    onSetStretchFar,
    onSetStretchExtentEdge,
    stretchRegion,
    originalObjectPosition,
    originalObjectState,
    originalWallElementPosition,
    originalWallState,
    pan,
    panRef,
    placementMode,
    previewObject,
    previewRotation,
    readOnly,
    resizeStartPos,
    roomDrawing,
    cabinetRunDrawing,
    recessedRowDrawing,
    wallChainDrawing,
    cabinetApplianceDrop,
    runDrag,
    isDraggingRun,
    setIsDraggingRun,
    setAppliancePreview,
    appliancePreviewRaf,
    appliancePreviewWorld,
    rotation,
    scale,
    scaleRef,
    selectedAtMouseDownRef,
    selectedItem,
    selectedRoom,
    setActiveResizeHandle,
    setActiveWallHandle,
    setAutoPanState,
    setDragStart,
    setHasDraggedMouse,
    setInternalSelectedItem,
    setIsDragging,
    setIsDraggingObject,
    setIsDraggingWall,
    setIsDraggingWallElement,
    setIsResizing,
    setMouseWorldPos,
    setObjectDragStart,
    setOriginalObjectPosition,
    setOriginalObjectState,
    setOriginalWallElementPosition,
    setOriginalWallState,
    setPan,
    setPreviewObject,
    setResizeStartPos,
    setScale,
    setSelectedFloorPlanMeasurement,
    setTempResizedObject,
    setWallDragStart,
    setWallElementDragStart,
    setWallRotationDebug,
    setWorldX,
    setWorldY,
    showRoomLabels,
    structureData,
    structureDoorsById,
    structureWalls,
    tempResizedObject,
    vertexAdd,
    vertexDelete,
    vertexMove,
    wallDragStart,
    wallElementDragStart,
  } = ctx;

  // === BEGIN VERBATIM (UVFloorPlanCanvas.js gesture region) ===
  // Which translate-rectangle edge is being dragged: 'near' | 'far' | 'min' |
  // 'max' | null.
  const draggingRegionEdgeRef = useRef(null);
  // Set when boundary drag starts, consumed once by the click handler so
  // the post-mouseup click doesn't fall through to wall selection.
  const suppressNextClickRef = useRef(false);
  // A wire's grab handle being dragged: which connection, and the last shape
  // the pointer produced, so mouse-up commits exactly what was previewed.
  const wireHandleDragRef = useRef(null);

  // Hit-test the four rectangle edges (in dir/perp projection space). Returns
  // the nearest edge key within `pixelTol`, or null. Edges are axis-aligned in
  // (dir, perp) space, so distance is just the projection difference, gated by
  // lying within the edge's span.
  const pickRegionEdge = (wx, wz, pixelTol = 10) => {
    if (!stretchRegion || !boundaryAxis?.dir) return null;
    const dir = boundaryAxis.dir;
    const perp = { x: -dir.z, z: dir.x };
    const d = wx * dir.x + wz * dir.z;
    const p = wx * perp.x + wz * perp.z;
    const { near, far, min, max } = stretchRegion;
    const tol = pixelTol / scale;
    const candidates = [];
    if (p >= min - tol && p <= max + tol) {
      candidates.push({ edge: 'near', dist: Math.abs(d - near) });
      candidates.push({ edge: 'far', dist: Math.abs(d - far) });
    }
    if (d >= near - tol && d <= far + tol) {
      candidates.push({ edge: 'min', dist: Math.abs(p - min) });
      candidates.push({ edge: 'max', dist: Math.abs(p - max) });
    }
    let best = null;
    for (const c of candidates) {
      if (c.dist <= tol && (!best || c.dist < best.dist)) best = c;
    }
    return best ? best.edge : null;
  };

  // Create pan handlers without memoization to avoid stale closures
  // Drag a bay's appliance ALONG ITS RUN — the body drag in Move, and the
  // move handle in any mode. A link that pins the bay (a sink centred on a
  // window) would be re-solved straight back, so it says so instead.
  const beginBayAlongRun = (object) => {
    const pin = runDrag.pinnedBy?.(object);
    if (pin) {
      runDrag.notify?.({
        message: `${object.label || object.type} is centred on ${
          (pin.fixedMembers || []).some((m) => m?.kind === 'opening')
            ? 'a window'
            : 'a wall'
        } — remove "Center on" in its panel to move it along the run.`,
        type: 'info',
      });
      return;
    }
    const hit = runDrag.bayOf(object);
    if (
      runDrag.begin({
        kind: 'bay',
        runId: hit.stored.id,
        bayId: hit.bay.id,
        roomId: hit.room?.id,
      })
    )
      setIsDraggingRun(true);
  };

  // Pick a plain run CABINET up and put it down along its run — written as
  // the place an edge drag writes (`useRunDrag`'s `cabinet-move`). The press
  // must travel before it counts, as a width drag's does, so a click on the
  // handle never moves anything.
  const beginCabinetMove = (object, from) => {
    if (
      runDrag.begin({
        kind: 'cabinet-move',
        runId: runDrag.runIdFor?.(object),
        objectId: object.id,
        roomId: runDrag.roomIdFor?.(object) ?? undefined,
        from,
        minTravel: BAY_WIDTH_DRAG_THRESHOLD_PX / scale,
      })
    )
      setIsDraggingRun(true);
  };

  const handleMouseDown = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // A wire's grab handle. Checked first: the handle sits ON the wire, so any
    // later handler would read the press as a click on the run — or as a pan.
    if (onWireShapeCommit && wiringHitRef?.current?.wireHandles?.size) {
      const rect = canvas.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      const { wireHandles, runs, screenPositions } = wiringHitRef.current;
      const reach = WIRE_HANDLE_RADIUS_PX + 4;
      for (const [connectionId, pt] of wireHandles) {
        if (Math.hypot(pt.x - px, pt.y - py) > reach) continue;
        const connection = (runs || [])
          .flatMap((r) => r.connections || [])
          .find((c) => c.id === connectionId);
        const from = connection && screenPositions?.get(connection.switchId);
        const to = connection && screenPositions?.get(connection.fixtureId);
        if (!from || !to) continue;
        wireHandleDragRef.current = { connectionId, from, to, shape: null };
        suppressNextClickRef.current = true;
        e.preventDefault?.();
        e.stopPropagation?.();
        return;
      }
    }

    // Stretch translate-rectangle drag — check before any other mouse-down
    // handling so dragging an edge doesn't accidentally select a wall behind
    // it.
    if (stretchRegion && boundaryAxis?.dir) {
      const rectBound = canvas.getBoundingClientRect();
      const wxz = screenToWorldCoordinates(
        e.clientX - rectBound.left,
        e.clientY - rectBound.top,
        canvas,
        pan,
        scale,
        rotation
      );
      const edge = pickRegionEdge(wxz.x, wxz.y);
      if (edge) {
        draggingRegionEdgeRef.current = edge;
        suppressNextClickRef.current = true;
        e.preventDefault?.();
        e.stopPropagation?.();
        return;
      }
    }

    // In 'both' mode, allow selection but no editing/dragging
    if (dataSource === 'both') {
      // Check if clicking on a wall for selection
      const rect = canvas.getBoundingClientRect();
      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;
      const worldCoords = screenToWorldCoordinates(
        screenX,
        screenY,
        canvas,
        pan,
        scale,
        rotation
      );

      // Check if clicked on a wall
      const dataToRender = getDataToRender(structureData);
      let wallsToCheck = [];
      if (dataToRender?.rooms) {
        wallsToCheck = getElementsWithRoomContext(dataToRender.rooms, 'walls');
      }
      if (dataToRender?.walls) {
        wallsToCheck = [...wallsToCheck, ...dataToRender.walls];
      }

      const clickedWall = wallsToCheck.find((wall) =>
        isPointNearWall(worldCoords.x, worldCoords.z, wall)
      );

      if (clickedWall) {
        // Allow wall selection in both mode
        const wallWithFlags = {
          ...clickedWall,
          isWall: true,
        };
        setInternalSelectedItem(wallWithFlags);
        if (onItemSelect) {
          onItemSelect(wallWithFlags);
        }
      } else {
        // Start panning if not clicking on a wall
        setIsDragging(true);
        isDraggingRef.current = true;
        dragStartRef.current = { x: e.clientX, y: e.clientY };
        setDragStart({ x: e.clientX, y: e.clientY });
        mouseMovementRef.current = 0;
        setHasDraggedMouse(false);
      }
      return;
    }

    // IMPORTANT: Capture what's selected RIGHT NOW before any other events fire
    // This needs to happen before click events might change the selection
    const itemSelectedAtStart = selectedItem;
    selectedAtMouseDownRef.current = itemSelectedAtStart;

    const rect = canvas.getBoundingClientRect();
    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;

    // Convert to world coordinates
    const worldCoords = screenToWorldCoordinates(
      screenX,
      screenY,
      canvas,
      pan,
      scale,
      rotation
    );

    // Check if clicking on a selected wall
    // Use the isWall helper which checks both room walls and structure walls
    const isSelectedWall = selectedItem && isWall(selectedItem);

    if (selectedItem && isSelectedWall) {
      const clickPoint = { x: worldCoords.x, z: worldCoords.y };

      // First check if clicking on a wall handle
      const wallHandle = hitTestWallHandles(clickPoint, selectedItem, scale, {
        coarse: grabHandlesEnabled,
      });

      if (
        wallHandle &&
        objectInteractionMode === 'resize' &&
        WALL_ENDPOINT_DRAG_ENABLED
      ) {
        // Start dragging wall endpoint
        console.log('✅ Starting wall handle drag:', {
          handle: wallHandle,
          wallId: selectedItem.id,
          currentCenter: selectedItem.center,
          currentDimensions: selectedItem.dimensions,
          currentRotation: selectedItem.rotation,
        });
        setIsDraggingWall(true);
        setActiveWallHandle(wallHandle);
        setWallDragStart(clickPoint);
        // Open the typed length editor on this wall, telling it which end is
        // under the cursor so the translate-rectangle covers THAT end.
        if (onWallLengthDragStart) {
          const hp = getWallEndPoints(selectedItem);
          onWallLengthDragStart(
            selectedItem,
            wallHandle === 'start' ? hp?.start : hp?.end
          );
        }
        // Store just the geometry state like object editing does
        setOriginalWallState({
          center: { ...selectedItem.center },
          dimensions: { ...selectedItem.dimensions },
          rotation: selectedItem.rotation
            ? { ...selectedItem.rotation }
            : undefined,
          isWallEdited: selectedItem.isWallEdited, // Store this flag too
          // Store polygon corners if present
          ...(selectedItem.polygonCorners && {
            polygonCorners: [...selectedItem.polygonCorners],
          }),
          id: selectedItem.id, // Keep id for debugging
        });
        return; // Don't start panning
      }

      // Check if clicking inside the wall body (for dragging the whole wall)
      // Only allow in move mode, and only while translating a wall is offered
      // at all — see WALL_BODY_DRAG_ENABLED.
      if (
        WALL_BODY_DRAG_ENABLED &&
        (objectInteractionMode === 'move' ||
          objectInteractionMode === 'move-group') &&
        isPointInObject(clickPoint, selectedItem)
      ) {
        // Start dragging the whole wall - maintain rotation
        setIsDraggingObject(true);
        setObjectDragStart(clickPoint);
        setOriginalObjectPosition({ ...selectedItem.center });
        return; // Don't start panning
      }
    }
    // Check if clicking on a selected wall-mounted element. Only with Move
    // armed: a door / window / opening used to drag the moment it was
    // selected, so a click meant to select or inspect it nudged it along
    // the wall — and on a trackpad the press-and-hold is easy to lose
    // mid-drag. Every other object already waited for the Move toggle;
    // openings now do too (2026-09-05). In resize mode, fall through to
    // the resize-handle hit-test branch below so handle drags are
    // interpreted as resizes.
    else if (
      isWallMountedOpening(selectedItem) &&
      objectInteractionMode === 'move'
    ) {
      // Check if this element was already selected when mouseDown happened
      // Compare against what was selected at the START of mouseDown
      const wasAlreadySelected =
        itemSelectedAtStart &&
        selectedItem &&
        itemSelectedAtStart.id &&
        selectedItem.id &&
        itemSelectedAtStart.id === selectedItem.id;

      // Also check if this item was JUST selected (within last 100ms)
      // This prevents dragging when selection just changed
      const timeSinceSelection = Date.now() - lastSelectionTimeRef.current;
      const justSelected = timeSinceSelection < 100; // 100ms threshold

      if (!wasAlreadySelected || justSelected) {
        // This element was NOT selected at mouseDown, or was JUST selected
        console.log('Wall element clicked but not starting drag', {
          wasAlreadySelected,
          justSelected,
          timeSinceSelection,
          wasSelected: itemSelectedAtStart?.id,
          nowSelected: selectedItem?.id,
        });
        // Let it fall through to normal selection
      } else {
        console.log(
          'Wall element clicked and WAS already selected - checking for drag start',
          {
            timeSinceSelection,
          }
        );
        const clickPoint = { x: worldCoords.x, z: worldCoords.y };

        // For wall elements, add some margin since they can be very thin
        const hitTestItem = {
          ...selectedItem,
          dimensions: {
            x: selectedItem.dimensions.x,
            y: selectedItem.dimensions.y,
            z: Math.max(selectedItem.dimensions.z, 0.3), // Ensure at least 30cm depth for hit testing
          },
        };

        // Check if clicking on the element — or its grab handle, the
        // finger-sized target a touch device shows beside it.
        if (
          isPointInObject(clickPoint, hitTestItem) ||
          (grabHandlesEnabled &&
            hitTestGrabHandle(
              clickPoint,
              selectedItem,
              scale,
              grabRoomCenter(selectedItem)
            ))
        ) {
          const elementType = selectedItem.category?.door
            ? 'door'
            : selectedItem.category?.window
              ? 'window'
              : selectedItem.category?.opening
                ? 'opening'
                : 'unknown';
          setIsDraggingWallElement(true);
          setWallElementDragStart(clickPoint);
          setOriginalWallElementPosition({ ...selectedItem.center });
          return; // Don't start panning
        }
      }
    }
    // Check if clicking on a selected object.
    // Exclude wall-mounted elements — they use the section above —
    // EXCEPT in resize mode, where they need to reach the resize-handle
    // hit-test below.
    else if (
      selectedItem &&
      !selectedItem.isRoomLabel &&
      !isWall(selectedItem) &&
      (objectInteractionMode === 'resize' ||
        !isWallMountedOpening(selectedItem))
    ) {
      const clickPoint = { x: worldCoords.x, z: worldCoords.y };

      // A cabinet run's END handle (a run-owned object is selected): a live
      // run re-solve, not an object resize.
      //
      // No mode term, and the DRAW call has none either — grabbing a handle is
      // unambiguous, so it needs nothing armed in front of it. A drag on the
      // object's BODY still does (`move` / `move-group`), because that gesture
      // could equally mean pan or select. The two halves must agree about
      // whether a handle is there, so they are changed together.
      const ownRunId = runDrag ? runDrag.runIdFor?.(selectedItem) : null;
      // The leg's MOVE handle, hit-tested before anything else: it is the only
      // affordance that says where to grab a 13-foot island, and it works
      // wherever the selected object happens to be.
      const runScope = cabinetEditScope === 'run';
      if (
        runScope &&
        runDrag?.hitTestMoveHandle?.(clickPoint, selectedItem, scale, {
          coarse: grabHandlesEnabled,
        })
      ) {
        const hit = runDrag.legOf(selectedItem);
        if (
          runDrag.begin({
            kind: 'leg',
            runId: hit.stored.id,
            segIndex: hit.segIndex,
            from: clickPoint,
            roomId: hit.room?.id,
          })
        )
          setIsDraggingRun(true);
        return;
      }

      if (runScope && ownRunId) {
        const endHandle = runDrag.hitTestEnd(clickPoint, selectedItem, scale, {
          coarse: grabHandlesEnabled,
        });
        if (endHandle) {
          // A pinned end (an appliance flush at the run's end, or the wall's
          // end) cannot move; say so instead of arming a drag that would
          // clamp straight back — which read as a handle that did nothing.
          if (endHandle.pinned) {
            runDrag.notify?.({ message: endHandle.reason, type: 'info' });
            return;
          }
          if (
            runDrag.begin({
              kind: 'end',
              runId: ownRunId,
              end: endHandle.end,
              // The run's room, resolved from the object — without it the
              // drag only worked with that room SELECTED (see roomIdFor).
              roomId: runDrag.roomIdFor?.(selectedItem) ?? undefined,
            })
          )
            setIsDraggingRun(true);
          return;
        }
      }

      // A BAY's own edge handle — the other scope, so it is asked only where
      // the run's handles are not drawn. Widens or narrows ONE box inside the
      // run; the packer re-flows the rest.
      const bayEdge = runScope
        ? null
        : runDrag?.hitTestBayEdge?.(clickPoint, selectedItem, scale, {
            coarse: grabHandlesEnabled,
          });
      if (bayEdge && ownRunId) {
        // A hard bay (a sink base, a scanned tall unit) is sized by what
        // stands in it, so its edge is drawn greyed. Say that rather than arm
        // a drag the commit would refuse — which it did, naming an unrelated
        // fixed width elsewhere in the run.
        if (bayEdge.pinned) {
          runDrag.notify?.({ message: bayEdge.reason, type: 'info' });
          return;
        }
        if (
          runDrag.begin({
            kind: 'bay-width',
            runId: ownRunId,
            objectId: selectedItem.id,
            side: bayEdge.side,
            roomId: runDrag.roomIdFor?.(selectedItem) ?? undefined,
            // Where the press landed, and how far it must travel to count as a
            // drag rather than a click. Converted to world units here because
            // the budget is a PIXEL one — it has to mean the same distance on
            // screen at every zoom, and `scale` lives on this side.
            from: clickPoint,
            minTravel: BAY_WIDTH_DRAG_THRESHOLD_PX / scale,
          })
        )
          setIsDraggingRun(true);
        return;
      }

      // The selected bay's MOVE handle: drags it along its run in ANY mode —
      // a press on the handle is unambiguous (`moveHandleOfferedFor`), where a
      // press on the body still waits for Move below. Drawn by the renderer
      // with the same predicate.
      if (
        runDrag &&
        moveHandleOfferedFor(selectedItem, runDrag, cabinetEditScope) &&
        hitTestGrabHandle(
          clickPoint,
          tempResizedObject || selectedItem,
          scale,
          grabRoomCenter(selectedItem)
        )
      ) {
        const object = tempResizedObject || selectedItem;
        if (runDrag.canDragBay(object)) beginBayAlongRun(object);
        else beginCabinetMove(object, clickPoint);
        return;
      }

      // Check physical resize handles (in resize mode — a loose fixture's own
      // grips still arm, and are the next thing to free). Pass the same
      // restricted handle set the draw call uses so the hit-test agrees
      // with what's on screen.
      // Gated on `runId` for the same reason the DRAW call is: a run-owned
      // object has no handles on screen, and a hit-test that still answered
      // would resize a box the next re-solve overwrites.
      const handle =
        objectInteractionMode === 'resize' && !ownRunId
          ? hitTestHandles(
              clickPoint,
              selectedItem,
              scale,
              canvasResizeHandlesFor(selectedItem),
              { coarse: grabHandlesEnabled }
            )
          : null;

      if (handle) {
        // Start resizing - use temp object as base if it exists
        const baseObject = tempResizedObject || selectedItem;
        setIsResizing(true);
        setActiveResizeHandle(handle);
        setResizeStartPos(clickPoint);
        setOriginalObjectState({
          center: { ...baseObject.center },
          dimensions: { ...baseObject.dimensions },
          rotation: baseObject.rotation
            ? { ...baseObject.rotation }
            : undefined,
        });
        // Don't clear temp resize yet - it will be replaced when we move
        return; // Don't start panning
      }

      // Check if clicking inside the object (for dragging).
      // ONLY start dragging if this object was already selected at mouseDown.
      // Special handling for sinks which use isPointInSinkBounds.
      const isInsideObject = (obj) => {
        const sinkLike = obj?.type && obj.type.includes('sink');
        return sinkLike
          ? isPointInSinkBounds(clickPoint.x, clickPoint.z, obj)
          : isPointInObject(clickPoint, obj);
      };
      let isClickingInsideObject =
        isInsideObject(selectedItem) ||
        (grabHandlesEnabled &&
          // Only where the handle is actually DRAWN — an island cabinet is
          // offered none, and answering for it made an invisible disc beside
          // the cabinet a second, undiscoverable way to start a leg drag.
          grabHandleOfferedFor(selectedItem, runDrag) &&
          hitTestGrabHandle(
            clickPoint,
            selectedItem,
            scale,
            grabRoomCenter(selectedItem)
          ));

      // In move-group, accept a mousedown anywhere on the move-group closure
      // as "inside the selection" so the user can grab the group from any
      // part (e.g. the deck of a tub, the cabinet of a vanity). Uses the
      // same free-move closure that the drag cascade and the secondary
      // highlight use. The drag-redirect logic below still picks the
      // canonical body for snap/collision.
      if (
        !isClickingInsideObject &&
        objectInteractionMode === 'move-group' &&
        structureData?.rooms
      ) {
        outer: for (const room of structureData.rooms) {
          if (!room.objects?.some((o) => o?.id === selectedItem.id)) continue;
          const moveGroup = getFreeMoveGroup(selectedItem.id, room);
          for (const obj of room.objects) {
            if (!obj || obj.id === selectedItem.id || !moveGroup.has(obj.id)) {
              continue;
            }
            if (isInsideObject(obj)) {
              isClickingInsideObject = true;
              break outer;
            }
          }
        }
      }

      if (isClickingInsideObject) {
        // Check if this element was already selected when mouseDown happened
        const wasAlreadySelected =
          selectedAtMouseDownRef.current &&
          selectedAtMouseDownRef.current.id &&
          selectedItem.id &&
          selectedAtMouseDownRef.current.id === selectedItem.id;

        // Also check timing for regular objects
        const timeSinceSelection = Date.now() - lastSelectionTimeRef.current;
        const justSelected = timeSinceSelection < 100;

        if (
          wasAlreadySelected &&
          !justSelected &&
          (objectInteractionMode === 'move' ||
            objectInteractionMode === 'move-group')
        ) {
          // Only start dragging in move/move-group mode, already selected, and not just selected
          // Wall-mounted elements use the constrained drag path.
          if (isWallMountedOpening(selectedItem)) {
            // This is a wall-mounted element, use wall element dragging
            setIsDraggingWallElement(true);
            setWallElementDragStart(clickPoint);
            setOriginalWallElementPosition({ ...selectedItem.center });
          } else {
            // Regular object dragging
            let baseObject = tempResizedObject || selectedItem;

            // An appliance that belongs to a cabinet run drags ALONG THE
            // RUN: its bay's anchor moves, the run re-solves live, and the
            // appliance (with its group) is moved by the re-solve — never a
            // free translation, and never the vanity-chain redirect below.
            // A FREE-STANDING leg picks the whole island up: its segment's
            // two endpoints translate together and the run re-solves live,
            // exactly like the end drag but with a delta instead of a param.
            // Checked before the bay drag because an island's sink is both
            // a bay AND a leg member, and "move the island" is what the
            // mode row offered.
            if (runDrag && runDrag.canDragLeg(baseObject)) {
              const hit = runDrag.legOf(baseObject);
              if (
                runDrag.begin({
                  kind: 'leg',
                  runId: hit.stored.id,
                  segIndex: hit.segIndex,
                  from: clickPoint,
                  roomId: hit.room?.id,
                })
              )
                setIsDraggingRun(true);
              return;
            }

            if (runDrag && runDrag.canDragBay(baseObject)) {
              beginBayAlongRun(baseObject);
              return;
            }
            // A plain run cabinet slides along its run too, put down where it
            // is dropped — the move handle's drag, from the body in Move.
            if (runDrag?.canPlaceCabinet?.(baseObject)) {
              beginCabinetMove(baseObject, clickPoint);
              return;
            }
            // Anything else a run owns (a cabinet, a countertop, an
            // appliance neither gesture above can serve) has no free
            // translation: its geometry is the run's solve, and a free move
            // would be undone by the next re-solve — or, in move-group,
            // would carry the whole installation off the wall. The bay
            // override / run-end handles are the affordances.
            //
            // Asked through `freeMoveRefusal`, not `baseObject.runId`: an
            // appliance sits in a bay WITHOUT carrying `runId` (#1875), so
            // the old test protected a run's cabinets and let its sink be
            // dragged off the island — the lower cabinet in the very same
            // bay was refused. It answers with a sentence because a press
            // that silently does nothing reads as a dead object, which is
            // how the neighbouring declines already behave.
            const refusal = runDrag?.freeMoveRefusal?.(baseObject);
            if (refusal) {
              runDrag.notify?.({ message: refusal.message, type: 'info' });
              return;
            }

            // If dragging a child in 'move-group' mode (or default for non-'move'),
            // redirect to drag the canonical body of the group instead.
            // In 'move' mode, drag just this object (no redirect).
            //
            // For tub groups, the support-box is the largest footprint and the
            // thing we want flush against walls / clear of other fixtures —
            // mirrors how vanity drag uses the lower-cabinet as the anchor.
            // Falls back to the bathtub for freestanding tubs (no surround).
            const tubGroupTypes = [
              'bathtub',
              'tub-surface',
              'tub-support-box',
              'bathroom-tub-faucet',
            ];
            dragRedirectTargetRef.current = null;
            const shouldRedirect = objectInteractionMode !== 'move';
            if (
              shouldRedirect &&
              baseObject.fixtureGroupId &&
              tubGroupTypes.includes(baseObject.type)
            ) {
              const objRoom = structureData?.rooms?.find((r) =>
                r.objects?.some((o) => o.id === baseObject.id)
              );
              if (objRoom) {
                const groupMembers =
                  objRoom.objects?.filter(
                    (o) => o.fixtureGroupId === baseObject.fixtureGroupId
                  ) || [];
                const supportBox = groupMembers.find(
                  (o) => o.type === 'tub-support-box'
                );
                const bathtub = groupMembers.find((o) => o.type === 'bathtub');
                // Corner tubs: use the bathtub (pentagon) as drag target,
                // not the rectangular support box which is larger.
                const isPolygonTubGroup = bathtub?.usePolygonSurround === true;
                const target = isPolygonTubGroup
                  ? bathtub || supportBox
                  : supportBox || bathtub;
                if (target && target.id !== baseObject.id) {
                  baseObject = {
                    ...target,
                    _roomId: objRoom.id,
                  };
                  dragRedirectTargetRef.current = baseObject;
                }
              }
            }

            // If dragging a sink/vanity child, redirect to the root parent
            // (e.g., faucet → sink → countertop → lower-cabinet)
            const sinkVanityChildTypes = [
              'bathroom-sink-faucet',
              'sink',
              'countertop',
            ];
            if (
              shouldRedirect &&
              !dragRedirectTargetRef.current &&
              sinkVanityChildTypes.some((t) => isObjectType(baseObject, t))
            ) {
              // Find the room containing this object
              const objRoom = structureData?.rooms?.find((r) =>
                r.objects?.some((o) => o.id === baseObject.id)
              );
              if (objRoom) {
                const chain = ParentChildSystem.getHierarchyChain(
                  baseObject.id,
                  objRoom
                );
                // Last element in chain is the root parent
                const root = chain[chain.length - 1];
                if (root && root.id !== baseObject.id) {
                  const rootObj = objRoom.objects?.find(
                    (o) => o.id === root.id
                  );
                  if (rootObj) {
                    baseObject = {
                      ...rootObj,
                      _roomId: objRoom.id,
                    };
                    dragRedirectTargetRef.current = baseObject;
                  }
                }
              }
            }

            setIsDraggingObject(true);
            setObjectDragStart(clickPoint);
            setOriginalObjectPosition({ ...baseObject.center });
            // Clear any temp resize object when starting drag
            setTempResizedObject(null);
          }
          return; // Don't start panning
        }
        // If not already selected, fall through to regular click handling
      }
    }

    // Otherwise start panning
    setIsDragging(true);
    isDraggingRef.current = true;
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    setDragStart({ x: e.clientX, y: e.clientY });
    mouseMovementRef.current = 0;
    setHasDraggedMouse(false);
  };

  const handleMouseMove = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;

    // Shaping a wire: the curve follows the pointer, and nothing else runs.
    if (wireHandleDragRef.current) {
      const drag = wireHandleDragRef.current;
      drag.shape = wireShapeFromHandle(
        drag.from.x,
        drag.from.y,
        drag.to.x,
        drag.to.y,
        { x: screenX, y: screenY },
        scale
      );
      onWireShapeDrag?.(drag.connectionId, drag.shape);
      return;
    }

    // Convert to world coordinates
    const worldCoords = screenToWorldCoordinates(
      screenX,
      screenY,
      canvas,
      pan,
      scale,
      rotation
    );

    // Stretch translate-rectangle edge drag — short-circuit before any other
    // movement handling so the dragged edge follows the mouse. near/far move
    // along `dir`; min/max move along `perp`.
    if (draggingRegionEdgeRef.current && boundaryAxis?.dir) {
      const dir = boundaryAxis.dir;
      const perp = { x: -dir.z, z: dir.x };
      const edge = draggingRegionEdgeRef.current;
      const projDir = worldCoords.x * dir.x + worldCoords.y * dir.z;
      const projPerp = worldCoords.x * perp.x + worldCoords.y * perp.z;
      if (edge === 'near' && onSetStretchBoundary)
        onSetStretchBoundary(projDir);
      else if (edge === 'far' && onSetStretchFar) onSetStretchFar(projDir);
      else if ((edge === 'min' || edge === 'max') && onSetStretchExtentEdge) {
        onSetStretchExtentEdge(edge, projPerp);
      }
      e.preventDefault?.();
      e.stopPropagation?.();
      return;
    }

    // Special handling for 'both' mode - only show pointer on walls
    if (dataSource === 'both' && !isDraggingRef.current) {
      const dataToRender = getDataToRender(structureData);
      let wallsToCheck = [];
      if (dataToRender?.rooms) {
        wallsToCheck = getElementsWithRoomContext(dataToRender.rooms, 'walls');
      }
      if (dataToRender?.walls) {
        wallsToCheck = [...wallsToCheck, ...dataToRender.walls];
      }

      const hoverWall = wallsToCheck.find((wall) =>
        isPointNearWall(worldCoords.x, worldCoords.y, wall)
      );

      canvas.style.cursor = hoverWall
        ? 'pointer'
        : isDraggingRef.current
          ? 'grabbing'
          : 'grab';

      // Skip other cursor logic in both mode
      if (!isDraggingRef.current) {
        return;
      }
    }

    // Update mouse world position for placement mode
    setMouseWorldPos({ x: worldCoords.x, z: worldCoords.y });

    // Handle vertex move mode — update hover or preview position
    if (vertexMove?.isActive) {
      vertexMove.handleMouseMove({
        x: worldCoords.x,
        z: worldCoords.y,
        shiftHeld: e.shiftKey,
      });
      if (vertexMove.selectedVertexIndex !== null) {
        canvas.style.cursor = 'grabbing';
      } else if (vertexMove.hoveredVertexIndex !== null) {
        canvas.style.cursor = 'grab';
      } else {
        canvas.style.cursor = 'default';
      }
      return;
    }

    // Handle vertex add mode — update hovered edge or placement preview
    if (vertexAdd?.isActive) {
      vertexAdd.handleMouseMove({
        x: worldCoords.x,
        z: worldCoords.y,
        shiftHeld: e.shiftKey,
      });
      if (vertexAdd.insertedVertexIndex !== null) {
        canvas.style.cursor = 'crosshair';
      } else {
        canvas.style.cursor =
          vertexAdd.hoveredEdgeIndex !== null ? 'crosshair' : 'default';
      }
      return;
    }

    // Handle vertex delete mode — update hovered vertex
    if (vertexDelete?.isActive) {
      vertexDelete.updateHoveredVertex({ x: worldCoords.x, z: worldCoords.y });
      canvas.style.cursor =
        vertexDelete.hoveredVertexIndex !== null ? 'crosshair' : 'default';
      return;
    }

    // Handle room drawing mode — update cursor position and skip standard preview
    if (placementMode?.type === 'room' && roomDrawing?.isActive) {
      roomDrawing.updateCursorPos(
        { x: worldCoords.x, z: worldCoords.y },
        e.shiftKey,
        scale
      );
      canvas.style.cursor = 'crosshair';
      return;
    }

    // Handle wall-chain drawing — the cursor is the end of the stretch being
    // drawn, snapped the way the next tap will be.
    if (placementMode?.type === 'wallChain' && wallChainDrawing?.isActive) {
      wallChainDrawing.updateCursorPos(
        { x: worldCoords.x, z: worldCoords.y },
        { shiftHeld: e.shiftKey }
      );
      canvas.style.cursor = 'crosshair';
      return;
    }

    // Handle recessed-row drawing — the cursor IS the row's far end, so the
    // preview can show the cans before the second click lands.
    if (placementMode?.type === 'recessedRow' && recessedRowDrawing?.isActive) {
      recessedRowDrawing.updateCursorPos({
        x: worldCoords.x,
        z: worldCoords.y,
      });
      canvas.style.cursor = 'crosshair';
      return;
    }

    // Handle callout placement — the cursor is where the cloud will hang, so
    // the preview can show the leader before the second click lands.
    if (placementMode?.type === 'callout' && calloutDrawing?.isActive) {
      calloutDrawing.updateCursorPos({ x: worldCoords.x, z: worldCoords.y });
      canvas.style.cursor = 'crosshair';
      return;
    }

    // Handle cabinet-run drawing mode — update cursor/snap and skip preview
    if (placementMode?.type === 'cabinetRun' && cabinetRunDrawing?.isActive) {
      cabinetRunDrawing.updateCursorPos(
        { x: worldCoords.x, z: worldCoords.y },
        e.shiftKey
      );
      canvas.style.cursor = 'crosshair';
      return;
    }

    // A run-end / bay drag in flight: re-solve the run at most once per
    // frame at the latest cursor position, into the ghost preview layer.
    if (isDraggingRun && runDrag) {
      // A run-end drag runs along the wall: the cursor says so at the
      // wall's angle, not a fixed ↔.
      canvas.style.cursor =
        runDrag.active?.kind === 'end'
          ? alongWallCursor(
              runDrag
                .handlesFor(selectedItem)
                .find((h) => h.end === runDrag.active.end)?.dir || [1, 0]
            )
          : 'grabbing';
      appliancePreviewWorld.current = { x: worldCoords.x, z: worldCoords.y };
      if (appliancePreviewRaf.current == null) {
        appliancePreviewRaf.current = requestAnimationFrame(() => {
          appliancePreviewRaf.current = null;
          const w = appliancePreviewWorld.current;
          setAppliancePreview(w ? runDrag.computePreview(w) || null : null);
        });
      }
      return;
    }

    // Handle cabinet-run appliance-drop mode — crosshair + live reflow preview
    // of the hovered run with the pending appliance dropped in.
    if (
      placementMode?.type === 'cabinetAppliance' &&
      cabinetApplianceDrop?.isActive
    ) {
      canvas.style.cursor = 'crosshair';
      // Stash the latest cursor pos; re-solve at most once per frame.
      appliancePreviewWorld.current = { x: worldCoords.x, z: worldCoords.y };
      if (appliancePreviewRaf.current == null) {
        appliancePreviewRaf.current = requestAnimationFrame(() => {
          appliancePreviewRaf.current = null;
          const w = appliancePreviewWorld.current;
          setAppliancePreview(
            w ? cabinetApplianceDrop.computePreview?.(w) || null : null
          );
        });
      }
      return;
    }

    // If in placement mode, update preview object position
    if (placementMode && setPreviewObject) {
      // Create or update preview object
      const defaultSize =
        placementMode.dimensions || getDefaultSize(placementMode.type);

      // Get the floor level for correct placement height
      const floorY = findFloorY(structureData);

      // Check if this is a door, window, opening, wall-light, mirror, bathroom fixture, or wall that needs special handling
      const bathroomFixtureTypes = [
        'toilet',
        'sink',
        'bathtub',
        'shower',
        'vanity',
      ];
      const isBathroomFixture = bathroomFixtureTypes.includes(
        placementMode.type
      );
      // Kitchen/utility fixtures that need wall snapping
      const kitchenFixtureTypes = [
        'lower-cabinet',
        'upper-cabinet',
        'fireplace',
        'washerDryer',
        'dishwasher',
        'fridge',
        // Both old (stove / oven) and new (range / wall-oven) names.
        'oven',
        'wall-oven',
        'stove',
        'range',
      ];
      const isKitchenFixture = kitchenFixtureTypes.includes(placementMode.type);
      const needsWallSnap =
        placementMode.category?.door ||
        placementMode.category?.window ||
        placementMode.category?.opening ||
        placementMode.type === 'wall-light' ||
        placementMode.type === 'overhead-wall-light' ||
        placementMode.type === 'mirror' ||
        isWallElectricalDevice(placementMode.type) ||
        isKitchenFixture ||
        isBathroomFixture;

      // Collect room walls tagged with room center for interior-side-only snapping
      let allWalls = [];
      const dataSource = structureData;
      if (dataSource?.rooms) {
        dataSource.rooms.forEach((room) => {
          if (room.walls) {
            const validWalls = room.walls.filter((wall) => wall != null);

            // Compute room center from wall centers for interior-side filtering
            let sumX = 0,
              sumZ = 0,
              count = 0;
            validWalls.forEach((w) => {
              if (w.center) {
                sumX += w.center.x;
                sumZ += w.center.z;
                count++;
              }
            });
            const roomCenter =
              count > 0 ? { x: sumX / count, z: sumZ / count } : null;

            // Tag each wall with its room id + center so snapping can pick the
            // host room's copy and enforce the interior side.
            const taggedWalls = validWalls.map((wall) => ({
              ...wall,
              _roomId: room.id,
              ...(roomCenter ? { _roomCenter: roomCenter } : {}),
            }));
            allWalls = allWalls.concat(taggedWalls);
          }
        });
      }

      // For doors/windows/openings, snap ONLY to structure walls
      const isOpeningType =
        placementMode.category?.door ||
        placementMode.category?.window ||
        placementMode.category?.opening;

      if (isOpeningType && structureWalls && structureWalls.length > 0) {
        allWalls = structureWalls
          .filter((w) => w != null)
          .map((w) => ({ ...w, _isStructureWallSnap: true }));
      }
      // Non-opening fixtures use room walls only, filtered to interior side by findClosestWall

      // Same host-room narrowing as the click path — see wallsForHostRoom.
      const hostRoom = isWallElectricalDevice(placementMode.type)
        ? findRoomContainingPoint(
            worldCoords.x,
            worldCoords.y,
            structureData?.rooms || []
          )
        : null;
      if (isWallElectricalDevice(placementMode.type)) {
        allWalls = wallsForHostRoom(allWalls, hostRoom?.id);
      }

      if (needsWallSnap && allWalls.length > 0) {
        // Try to snap to nearest wall
        // Bathroom/kitchen fixtures use their type directly for snapping config lookup
        const elementType = placementMode.category?.door
          ? 'door'
          : placementMode.category?.window
            ? 'window'
            : placementMode.type === 'wall-light'
              ? 'wall-light'
              : placementMode.type === 'mirror'
                ? 'mirror'
                : isKitchenFixture ||
                    isBathroomFixture ||
                    isWallElectricalDevice(placementMode.type)
                  ? placementMode.type
                  : 'opening';

        const snapResult = snapElementToWall(
          { x: worldCoords.x, z: worldCoords.y },
          allWalls,
          defaultSize,
          elementType,
          floorY // Pass the actual floor Y position
        );

        if (snapResult && snapResult.wall) {
          // Get wall thickness for preview
          // Use the actual thickness, even if it's 0. Only use default if dimensions don't exist
          const wallThickness =
            snapResult.wall.dimensions?.z !== undefined
              ? snapResult.wall.dimensions.z
              : 0.1;

          // Keep zero thickness as zero - don't add minimum

          // For wall-lights, mirrors, bathroom fixtures, and kitchen fixtures, keep their original dimensions
          // For doors/windows/openings, match wall thickness.
          // For demoed walls, also match wall HEIGHT — a demoed section
          // is a whole-wall demolition, so the stored y should reflect
          // the parent wall's actual height, not the 8' placement default
          // (which reads back as "94 inches" in the panel even on a 9' wall).
          const adjustedDimensions =
            elementType === 'wall-light' ||
            elementType === 'mirror' ||
            isWallElectricalDevice(placementMode.type) ||
            isKitchenFixture ||
            isBathroomFixture
              ? defaultSize // Wall lights, mirrors, bathroom fixtures, and kitchen fixtures keep their own dimensions
              : {
                  ...defaultSize,
                  z: wallThickness, // Doors/windows/openings match wall thickness
                  ...(isDemoedOpening(placementMode) &&
                  snapResult.wall.dimensions?.y
                    ? { y: snapResult.wall.dimensions.y }
                    : {}),
                };

          // For demoed walls, vertically center on the parent wall
          // (and match its height — see adjustedDimensions above).
          const adjustedCenter =
            isDemoedOpening(placementMode) &&
            snapResult.wall.center?.y !== undefined
              ? { ...snapResult.position, y: snapResult.wall.center.y }
              : snapResult.position;

          // Whole-wall demo preview: highlight the ENTIRE clicked-room wall span
          // (dashed rect) using the same span math as the click handler, so
          // preview == what gets removed.
          if (placementMode.type === 'wholeWall') {
            const span = computeClickedRoomWallSpan(
              { wall: snapResult.wall, position: snapResult.position },
              structureData?.rooms || []
            );
            if (span) {
              setPreviewObject({
                type: 'wholeWall',
                label: placementMode.label,
                category: { opening: { demoed: true } },
                center: {
                  x: span.center.x,
                  y: snapResult.wall.center?.y ?? span.center.y,
                  z: span.center.z,
                },
                dimensions: span.dimensions,
                rotation: span.rotation,
                isPreview: true,
                isSnappedToWall: true,
                parentWall: snapResult.wall,
              });
              return;
            }
            // No room-wall span at cursor: fall through to the generic demoed
            // ghost below (uses adjustedCenter + adjustedDimensions which
            // already handle demoed-opening positioning correctly).
          }

          // Element is snapped to wall
          setPreviewObject({
            type: placementMode.type,
            label: placementMode.label,
            category: placementMode.category,
            center: adjustedCenter,
            dimensions: adjustedDimensions,
            rotation: isWallElectricalDevice(placementMode.type)
              ? {
                  ...snapResult.rotation,
                  y: faceWallDeviceIntoRoom(
                    snapResult.rotation?.y || 0,
                    adjustedCenter,
                    roomForSnappedWall(snapResult.wall, structureData?.rooms)
                  ),
                }
              : snapResult.rotation,
            isPreview: true,
            isSnappedToWall: true,
            parentWall: snapResult.wall, // Local variable for preview only
          });
        } else {
          // No wall nearby - show preview at cursor but with visual indicator
          setPreviewObject({
            type: placementMode.type,
            label: placementMode.label,
            category: placementMode.category,
            center: {
              x: worldCoords.x,
              y:
                elementType === 'window'
                  ? floorY + 1.2
                  : floorY + defaultSize.y / 2,
              z: worldCoords.y,
            },
            dimensions: defaultSize,
            rotation: { x: 0, y: previewRotation, z: 0 },
            isPreview: true,
            isSnappedToWall: false,
            needsWall: true, // Visual indicator that it needs a wall
          });
        }
      } else {
        // Regular object placement (not door/window/opening/wall)
        let objectDimensions = defaultSize;
        let targetCabinet = null;

        // Smart sizing for countertop placement
        if (placementMode.type === 'countertop') {
          // Check if cursor is over a lower cabinet
          const allObjects = [];
          if (structureData?.rooms) {
            structureData.rooms.forEach((room) => {
              if (room.objects) {
                allObjects.push(
                  ...room.objects.map((obj) => ({
                    ...obj,
                    _roomId: room.id,
                  }))
                );
              }
            });
          }

          // Find objects that can have a countertop placed on top (cabinets, fireplaces, etc.)
          for (const obj of allObjects) {
            const objType =
              obj.type || (obj.category && Object.keys(obj.category)[0]) || '';
            if (canHaveCountertopOnTop(objType)) {
              const objCenter = obj.center || obj.position;
              const objDims = obj.dimensions;
              if (objCenter && objDims) {
                // Check if cursor is within object bounds
                const halfX = objDims.x / 2;
                const halfZ = objDims.z / 2;
                const inBounds =
                  worldCoords.x >= objCenter.x - halfX &&
                  worldCoords.x <= objCenter.x + halfX &&
                  worldCoords.y >= objCenter.z - halfZ &&
                  worldCoords.y <= objCenter.z + halfZ;

                if (inBounds) {
                  targetCabinet = obj;
                  // Size countertop to match parent + 1/2" overhang on all sides
                  const OVERHANG_METERS = 0.0127; // 0.5 inches
                  objectDimensions = {
                    x: objDims.x + 2 * OVERHANG_METERS,
                    y: 0.038, // 1.5 inches thick
                    z: objDims.z + 2 * OVERHANG_METERS,
                  };
                  break;
                }
              }
            }
          }
        }

        // Smart positioning for sink placement - snap to 3.5" from front edge
        let previewCenter = {
          x: worldCoords.x,
          y: floorY + objectDimensions.y / 2, // Place object on floor (bottom at floor level)
          z: worldCoords.y,
        };

        const isSinkType = isSink({ type: placementMode.type });

        if (isSinkType) {
          // Find countertop at cursor position
          const countertops = [];
          if (structureData?.rooms) {
            structureData.rooms.forEach((room) => {
              if (room.objects) {
                room.objects.forEach((obj) => {
                  if (obj.type === 'countertop' || obj.category?.countertop) {
                    countertops.push({
                      ...obj,
                      roomId: room.id,
                    });
                  }
                });
              }
            });
          }

          // Find countertop at this position. Rotation-aware: a slab's stored
          // dimensions are in its OWN frame, so testing them against world
          // axes checks a box turned by the slab's rotation.
          const targetCountertop = countertopAtPoint(
            worldCoords.x,
            worldCoords.y,
            countertops
          );

          if (targetCountertop) {
            // Calculate snapped position 3.5" from front edge
            const FRONT_EDGE_OFFSET = 0.0889; // 3.5 inches in meters
            const SINK_DEPTH = 0.457; // 18 inches standard sink depth in meters

            const ctDims = Array.isArray(targetCountertop.dimensions)
              ? {
                  x: targetCountertop.dimensions[0],
                  y: targetCountertop.dimensions[1],
                  z: targetCountertop.dimensions[2],
                }
              : targetCountertop.dimensions;

            // Cabinet model space: +Z is front (doors), -Z is back (wall-facing)
            const rotation = targetCountertop.rotation || { x: 0, y: 0, z: 0 };
            const rotationY = rotation.y || 0;

            let sinkX = worldCoords.x;
            let sinkZ = worldCoords.y;

            // Normalize rotation to 0-360 degrees
            const normalizedRotation = ((rotationY % 360) + 360) % 360;

            if (normalizedRotation >= 315 || normalizedRotation < 45) {
              // rotation.y ≈ 0°: Cabinet faces +Z, front edge at max Z, back at min Z
              // Sink front edge should be 3.5" inward from cabinet front edge
              // Sink center = (cabinet front edge - 3.5") - (sink depth / 2)
              const cabinetFrontEdgeZ =
                targetCountertop.center.z + ctDims.z / 2;
              const sinkFrontEdgeZ = cabinetFrontEdgeZ - FRONT_EDGE_OFFSET;
              sinkZ = sinkFrontEdgeZ - SINK_DEPTH / 2;
            } else if (normalizedRotation >= 45 && normalizedRotation < 135) {
              // rotation.y ≈ 90°: Cabinet faces -X, front edge at min X, back at max X
              // Sink center = (cabinet front edge + 3.5") + (sink depth / 2)
              const cabinetFrontEdgeX =
                targetCountertop.center.x - ctDims.x / 2;
              const sinkFrontEdgeX = cabinetFrontEdgeX + FRONT_EDGE_OFFSET;
              sinkX = sinkFrontEdgeX + SINK_DEPTH / 2;
            } else if (normalizedRotation >= 135 && normalizedRotation < 225) {
              // rotation.y ≈ 180°: Cabinet faces -Z, front edge at min Z, back at max Z
              // Sink center = (cabinet front edge + 3.5") + (sink depth / 2)
              const cabinetFrontEdgeZ =
                targetCountertop.center.z - ctDims.z / 2;
              const sinkFrontEdgeZ = cabinetFrontEdgeZ + FRONT_EDGE_OFFSET;
              sinkZ = sinkFrontEdgeZ + SINK_DEPTH / 2;
            } else {
              // rotation.y ≈ 270°: Cabinet faces +X, front edge at max X, back at min X
              // Sink center = (cabinet front edge - 3.5") - (sink depth / 2)
              const cabinetFrontEdgeX =
                targetCountertop.center.x + ctDims.x / 2;
              const sinkFrontEdgeX = cabinetFrontEdgeX - FRONT_EDGE_OFFSET;
              sinkX = sinkFrontEdgeX - SINK_DEPTH / 2;
            }

            previewCenter = {
              x: sinkX,
              y: targetCountertop.center.y,
              z: sinkZ,
            };
          }
        }

        setPreviewObject({
          type: placementMode.type,
          label: placementMode.label,
          category: placementMode.category,
          center: previewCenter,
          dimensions: objectDimensions,
          rotation: { x: 0, y: previewRotation, z: 0 },
          isPreview: true,
          targetCabinet: targetCabinet, // Store reference to target cabinet for placement
        });
      }
    }

    // WALL END-HANDLE DRAG → the typed length pathway.
    //
    // The handle used to move freely in 2D, which re-angled the wall and every
    // wall meeting the moved junction. Now the cursor is projected onto the
    // wall's own axis and supplies nothing but a LENGTH DELTA; the parent feeds
    // that to the same `computeStretch` a typed value goes through, so the
    // wall's angle is preserved by construction. Nothing is written here — the
    // stretch preview is what the canvas draws.
    if (
      selectedItem &&
      isDraggingWall &&
      wallDragStart &&
      originalWallState &&
      activeWallHandle
    ) {
      if (onWallLengthDrag) {
        onWallLengthDrag(
          wallLengthDelta(originalWallState, activeWallHandle, {
            x: worldCoords.x,
            z: worldCoords.y,
          })
        );
      }
      canvas.style.cursor = 'ew-resize';
      return;
    }

    // Handle wall element dragging (doors, windows, openings)
    if (
      selectedItem &&
      isDraggingWallElement &&
      wallElementDragStart &&
      originalWallElementPosition &&
      selectedItem.parentWallIds?.[0]
    ) {
      // Debug logging removed
      // Find the parent wall
      let parentWall = null;

      // Check structure-level walls first (for structure-level openings in bold mode)
      if (structureData?.walls) {
        parentWall = structureData.walls.find(
          (w) => w?.id === selectedItem.parentWallIds[0]
        );
      }

      // Then check room walls
      if (!parentWall && structureData?.rooms) {
        for (const room of structureData.rooms) {
          if (room.walls) {
            parentWall = room.walls.find(
              (w) => w?.id === selectedItem.parentWallIds[0]
            );
            if (parentWall) break;
          }
        }
      }

      if (parentWall) {
        // Project the current mouse position onto the wall line
        const mousePoint = { x: worldCoords.x, z: worldCoords.y };

        // Get wall start and end points (from wallSnappingUtils logic)
        let wallStart, wallEnd;
        if (!parentWall.start && !parentWall.end && parentWall.center) {
          // Calculate start and end from center, dimensions, and rotation
          // Use the same rotation method as the drawing code (apply3DRotationTo2D)
          const halfLength = (parentWall.dimensions?.x || 1) / 2;

          // Use apply3DRotationTo2D for consistent rotation handling
          const wallDir = apply3DRotationTo2D(parentWall.rotation);

          wallStart = {
            x: parentWall.center.x - wallDir[0] * halfLength,
            z: parentWall.center.z - wallDir[1] * halfLength,
          };
          wallEnd = {
            x: parentWall.center.x + wallDir[0] * halfLength,
            z: parentWall.center.z + wallDir[1] * halfLength,
          };
        } else if (parentWall.start && parentWall.end) {
          wallStart = { x: parentWall.start.x, z: parentWall.start.z };
          wallEnd = { x: parentWall.end.x, z: parentWall.end.z };
        }

        if (wallStart && wallEnd) {
          // Calculate wall vector
          const wallVector = {
            x: wallEnd.x - wallStart.x,
            z: wallEnd.z - wallStart.z,
          };
          const wallLength = Math.sqrt(
            wallVector.x * wallVector.x + wallVector.z * wallVector.z
          );
          const wallDir = {
            x: wallVector.x / wallLength,
            z: wallVector.z / wallLength,
          };

          // Project mouse point onto wall line
          const toMouse = {
            x: mousePoint.x - wallStart.x,
            z: mousePoint.z - wallStart.z,
          };
          const mouseProjection = toMouse.x * wallDir.x + toMouse.z * wallDir.z;

          // Calculate the grab offset: difference between where user clicked
          // and the element center, projected onto the wall direction
          const toOrigCenter = {
            x: originalWallElementPosition.x - wallStart.x,
            z: originalWallElementPosition.z - wallStart.z,
          };
          const centerProjection =
            toOrigCenter.x * wallDir.x + toOrigCenter.z * wallDir.z;
          const toDragStart = {
            x: wallElementDragStart.x - wallStart.x,
            z: wallElementDragStart.z - wallStart.z,
          };
          const dragStartProjection =
            toDragStart.x * wallDir.x + toDragStart.z * wallDir.z;
          const grabOffset = dragStartProjection - centerProjection;

          // Apply grab offset so element doesn't jump to mouse position
          const projection = mouseProjection - grabOffset;

          // Clamp to wall bounds (with some margin for the element width)
          const elementHalfWidth = (selectedItem.dimensions?.x || 1) / 2;
          const clampedProjection = Math.max(
            elementHalfWidth,
            Math.min(wallLength - elementHalfWidth, projection)
          );

          // Calculate new position along wall
          const newPosition = {
            x: wallStart.x + wallDir.x * clampedProjection,
            y: selectedItem.center.y, // Keep Y unchanged
            z: wallStart.z + wallDir.z * clampedProjection,
          };

          // Get wall thickness and update dimensions
          const wallThickness =
            parentWall.dimensions?.z !== undefined
              ? parentWall.dimensions.z
              : 0.1;

          // Create updated element with new position
          const draggedElement = {
            ...selectedItem,
            center: newPosition,
            dimensions: {
              ...selectedItem.dimensions,
              z: wallThickness, // Update thickness to match wall
            },
            isDragging: true, // Mark as currently dragging
          };

          const elementType = selectedItem.category?.door
            ? 'door'
            : selectedItem.category?.window
              ? 'window'
              : selectedItem.category?.opening
                ? 'opening'
                : 'unknown';

          // Update parent immediately for single source of truth
          if (onItemSelect) {
            onItemSelect(draggedElement);
          }
        }
      }

      canvas.style.cursor = 'move';
      return;
    }

    // Handle object dragging (including whole wall dragging)
    if (
      selectedItem &&
      isDraggingObject &&
      objectDragStart &&
      originalObjectPosition
    ) {
      // Use redirected drag target (e.g., parent bathtub) if set
      const effectiveDragItem = dragRedirectTargetRef.current || selectedItem;

      // Defensive: a wall-mounted element that ended up in regular
      // object dragging gets the same wall-axis constraints as if it
      // had taken the isDraggingWallElement path. Without this branch
      // the user can drag perpendicular off the wall.
      if (isWallMountedOpening(effectiveDragItem)) {
        // Find the parent wall
        let parentWall = null;

        // Check structure-level walls first (for structure-level openings in bold mode)
        if (structureData?.walls) {
          parentWall = structureData.walls.find(
            (w) => w?.id === selectedItem.parentWallIds[0]
          );
        }

        // Then check room walls
        if (!parentWall && structureData?.rooms) {
          for (const room of structureData.rooms) {
            if (room.walls) {
              parentWall = room.walls.find(
                (w) => w?.id === selectedItem.parentWallIds[0]
              );
              if (parentWall) break;
            }
          }
        }

        if (parentWall) {
          // Project the current mouse position onto the wall line
          const mousePoint = { x: worldCoords.x, z: worldCoords.y };

          // Get wall start and end points
          let wallStart, wallEnd;
          if (!parentWall.start && !parentWall.end && parentWall.center) {
            // Calculate start and end from center, dimensions, and rotation
            // Use the same rotation method as the drawing code (apply3DRotationTo2D)
            const halfLength = (parentWall.dimensions?.x || 1) / 2;

            // Use apply3DRotationTo2D for consistent rotation handling
            const wallDir = apply3DRotationTo2D(parentWall.rotation);

            wallStart = {
              x: parentWall.center.x - wallDir[0] * halfLength,
              z: parentWall.center.z - wallDir[1] * halfLength,
            };
            wallEnd = {
              x: parentWall.center.x + wallDir[0] * halfLength,
              z: parentWall.center.z + wallDir[1] * halfLength,
            };
          } else if (parentWall.start && parentWall.end) {
            wallStart = { x: parentWall.start.x, z: parentWall.start.z };
            wallEnd = { x: parentWall.end.x, z: parentWall.end.z };
          }

          if (wallStart && wallEnd) {
            // Calculate wall vector
            const wallVector = {
              x: wallEnd.x - wallStart.x,
              z: wallEnd.z - wallStart.z,
            };
            const wallLength = Math.sqrt(
              wallVector.x * wallVector.x + wallVector.z * wallVector.z
            );
            const wallDir = {
              x: wallVector.x / wallLength,
              z: wallVector.z / wallLength,
            };

            // Project mouse point onto wall line
            const toMouse = {
              x: mousePoint.x - wallStart.x,
              z: mousePoint.z - wallStart.z,
            };
            const mouseProjection =
              toMouse.x * wallDir.x + toMouse.z * wallDir.z;

            // Calculate the grab offset: difference between where user clicked
            // and the element center, projected onto the wall direction
            const toOrigCenter = {
              x: originalObjectPosition.x - wallStart.x,
              z: originalObjectPosition.z - wallStart.z,
            };
            const centerProjection =
              toOrigCenter.x * wallDir.x + toOrigCenter.z * wallDir.z;
            const toDragStart = {
              x: objectDragStart.x - wallStart.x,
              z: objectDragStart.z - wallStart.z,
            };
            const dragStartProjection =
              toDragStart.x * wallDir.x + toDragStart.z * wallDir.z;
            const grabOffset = dragStartProjection - centerProjection;

            // Apply grab offset so element doesn't jump to mouse position
            const projection = mouseProjection - grabOffset;

            // Clamp to wall bounds
            const elementHalfWidth = (selectedItem.dimensions?.x || 1) / 2;
            const clampedProjection = Math.max(
              elementHalfWidth,
              Math.min(wallLength - elementHalfWidth, projection)
            );

            // Calculate new position along wall
            const newPosition = {
              x: wallStart.x + wallDir.x * clampedProjection,
              y: selectedItem.center.y,
              z: wallStart.z + wallDir.z * clampedProjection,
            };

            // Get wall thickness
            const wallThickness =
              parentWall.dimensions?.z !== undefined
                ? parentWall.dimensions.z
                : 0.1;

            // Create constrained dragged element
            const draggedElement = {
              ...selectedItem,
              center: newPosition,
              dimensions: {
                ...selectedItem.dimensions,
                z: wallThickness,
              },
              isDragging: true,
            };

            // Update parent immediately
            if (onItemSelect) {
              onItemSelect(draggedElement);
            }
            canvas.style.cursor = 'grabbing'; // Closed hand while dragging wall element
            return;
          }
        }
        // If we couldn't find the parent wall, fall through to regular dragging
        // Could not find parent wall for constraint
      }

      // Regular object dragging (not wall-constrained)
      const dragDelta = {
        x: worldCoords.x - objectDragStart.x,
        z: worldCoords.y - objectDragStart.z,
      };

      // Calculate new center position
      let newCenter = {
        x: originalObjectPosition.x + dragDelta.x,
        y: originalObjectPosition.y, // Keep Y unchanged
        z: originalObjectPosition.z + dragDelta.z,
      };

      // Check if we're dragging a wall
      if (isWall(selectedItem)) {
        // For walls, update both center and start/end points
        const halfLength = selectedItem.dimensions.x / 2;
        const rotation = selectedItem.rotation?.y || 0;
        const cos = Math.cos(rotation);
        const sin = Math.sin(rotation);

        // Keep original wall thickness (even if zero)
        const wallThickness = selectedItem.dimensions?.z || WALL_THICKNESS;

        // Calculate endpoints at the new position (before snapping)
        let wallCenter = { ...newCenter };
        const wallDirX = cos;
        const wallDirZ = -sin;

        // Calculate both endpoints
        let startPoint = {
          x: wallCenter.x - halfLength * wallDirX,
          z: wallCenter.z - halfLength * wallDirZ,
        };
        let endPoint = {
          x: wallCenter.x + halfLength * wallDirX,
          z: wallCenter.z + halfLength * wallDirZ,
        };

        // Try to snap wall endpoints to nearby structure walls
        if (structureWalls && structureWalls.length > 0) {
          const endpoints = [
            { point: startPoint, type: 'start' },
            { point: endPoint, type: 'end' },
          ];

          let bestSnap = null;
          let bestDistance = 0.3; // ~1 foot snap distance

          for (const { point, type } of endpoints) {
            const snapResult = snapWallEndpointToWall(point, structureWalls, {
              snapDistance: 0.3,
              excludeWallId: selectedItem.id,
              newWallThickness: wallThickness,
            });

            if (snapResult && snapResult.distance < bestDistance) {
              // For surface snaps: must be roughly perpendicular
              // For end cap snaps: can be perpendicular OR parallel
              const targetWallDir = snapResult.targetWallDir;
              if (targetWallDir) {
                const dotProduct = Math.abs(
                  wallDirX * targetWallDir.x + wallDirZ * targetWallDir.z
                );
                const perpendicularTolerance = Math.PI / 6; // ~30 degrees
                const isRoughlyPerpendicular =
                  dotProduct < Math.sin(perpendicularTolerance);
                const isRoughlyParallel =
                  dotProduct > Math.cos(perpendicularTolerance);

                let shouldSnap = false;
                if (snapResult.snapType === 'endcap') {
                  shouldSnap = isRoughlyPerpendicular || isRoughlyParallel;
                } else {
                  shouldSnap = isRoughlyPerpendicular;
                }

                if (shouldSnap) {
                  bestDistance = snapResult.distance;

                  // Choose appropriate snap point for end cap snaps
                  let snappedPoint;
                  if (snapResult.snapType === 'endcap') {
                    snappedPoint = isRoughlyParallel
                      ? snapResult.extendSnapPoint || snapResult.point
                      : snapResult.cornerSnapPoint || snapResult.point;
                  } else {
                    snappedPoint = snapResult.point;
                  }

                  bestSnap = { snappedPoint, type };
                }
              }
            }
          }

          // Apply the snap if found
          if (bestSnap) {
            const { snappedPoint, type } = bestSnap;
            if (type === 'start') {
              // Adjust center based on snapped start point
              wallCenter = {
                x: snappedPoint.x + halfLength * wallDirX,
                y: newCenter.y,
                z: snappedPoint.z + halfLength * wallDirZ,
              };
            } else {
              // Adjust center based on snapped end point
              wallCenter = {
                x: snappedPoint.x - halfLength * wallDirX,
                y: newCenter.y,
                z: snappedPoint.z - halfLength * wallDirZ,
              };
            }
          }
        }

        const draggedWall = {
          ...selectedItem,
          center: wallCenter,
          dimensions: {
            ...selectedItem.dimensions,
            z: wallThickness, // Preserve original thickness
          },
          start: {
            x: wallCenter.x - halfLength * cos,
            y: wallCenter.y - selectedItem.dimensions.y / 2,
            z: wallCenter.z + halfLength * sin, // + sin for correct Z direction
          },
          end: {
            x: wallCenter.x + halfLength * cos,
            y: wallCenter.y + selectedItem.dimensions.y / 2,
            z: wallCenter.z - halfLength * sin, // - sin for correct Z direction
          },
          isDragging: true,
        };

        // Store for use at drag-end. `selectedItem` no longer updates during
        // a wall drag — the parent routes wall gestures into the plan chassis
        // instead of writing state per frame — so without this the mouseup
        // handler compares the ORIGINAL centre against itself, decides the
        // wall never moved, and publishes `isMoved: false`.
        lastDraggedObjectRef.current = draggedWall;

        // Update parent immediately
        if (onItemSelect) {
          onItemSelect(draggedWall);
        }

        // Update cursor to show we're dragging (closed hand)
        canvas.style.cursor = 'grabbing';
      } else {
        // Check if this object type should snap to room walls during drag
        const objType = effectiveDragItem.type
          ? effectiveDragItem.type.toLowerCase()
          : effectiveDragItem.category
            ? Object.keys(effectiveDragItem.category)[0]?.toLowerCase()
            : '';
        const wallSnapDragTypes = [
          'toilet',
          'tub-support-box',
          'shower',
          'lower-cabinet',
          'upper-cabinet',
          'fireplace',
          'washerDryer',
          'dishwasher',
          'fridge',
          // Both old (stove / oven) and new (range / wall-oven) names.
          'oven',
          'wall-oven',
          'stove',
          'range',
          'mirror',
          'vanity',
          'wall-light',
          'overhead-wall-light',
        ];
        const shouldSnapToWall = wallSnapDragTypes.includes(objType);

        // Center-snap: when the dragged object isn't a wall-snap type, snap
        // its center to the largest sibling in its move-group (e.g. bathtub
        // → support-box, sink → vanity/lower-cabinet) on each axis
        // independently. Snap tolerance is 5cm in world units.
        if (!shouldSnapToWall && structureData?.rooms) {
          const centerSnapRoom =
            (effectiveDragItem._roomId
              ? structureData.rooms.find(
                  (r) => r.id === effectiveDragItem._roomId
                )
              : null) ||
            structureData.rooms.find((r) =>
              r.objects?.some((o) => o?.id === effectiveDragItem.id)
            );
          if (centerSnapRoom?.objects) {
            const moveGroup = getFreeMoveGroup(
              effectiveDragItem.id,
              centerSnapRoom
            );
            let anchor = null;
            let bestArea = 0;
            for (const obj of centerSnapRoom.objects) {
              if (
                !obj ||
                obj.id === effectiveDragItem.id ||
                !moveGroup.has(obj.id) ||
                !obj.center ||
                !obj.dimensions
              ) {
                continue;
              }
              const area = (obj.dimensions.x || 0) * (obj.dimensions.z || 0);
              if (area > bestArea) {
                anchor = obj;
                bestArea = area;
              }
            }
            if (anchor) {
              const SNAP_TOL = 0.05;
              const snappedX =
                Math.abs(newCenter.x - anchor.center.x) < SNAP_TOL
                  ? anchor.center.x
                  : newCenter.x;
              const snappedZ =
                Math.abs(newCenter.z - anchor.center.z) < SNAP_TOL
                  ? anchor.center.z
                  : newCenter.z;
              if (snappedX !== newCenter.x || snappedZ !== newCenter.z) {
                newCenter = { ...newCenter, x: snappedX, z: snappedZ };
              }
            }
          }
        }

        let draggedObject;

        if (shouldSnapToWall) {
          // Collect room walls tagged with room center for interior-side snapping
          let allWalls = [];
          if (structureData?.rooms) {
            structureData.rooms.forEach((room) => {
              if (room.walls) {
                const validWalls = room.walls.filter((w) => w != null);
                let sumX = 0,
                  sumZ = 0,
                  count = 0;
                validWalls.forEach((w) => {
                  if (w.center) {
                    sumX += w.center.x;
                    sumZ += w.center.z;
                    count++;
                  }
                });
                const roomCenter =
                  count > 0 ? { x: sumX / count, z: sumZ / count } : null;
                const taggedWalls = validWalls.map((wall) => ({
                  ...wall,
                  ...(roomCenter ? { _roomCenter: roomCenter } : {}),
                }));
                allWalls = allWalls.concat(taggedWalls);
              }
            });
          }

          if (allWalls.length > 0) {
            // Use actual floor Y, not derived from object position (prevents accumulation)
            const floorY = findFloorY(structureData);

            const snapDimensions =
              effectiveDragItem.dimensions || getDefaultSize(objType);

            const snapResult = snapElementToWall(
              { x: newCenter.x, z: newCenter.z },
              allWalls,
              snapDimensions,
              objType,
              floorY
            );

            if (snapResult && snapResult.wall) {
              draggedObject = {
                ...effectiveDragItem,
                center: {
                  ...snapResult.position,
                  y: effectiveDragItem.center.y, // Preserve original Y during floor plan drag
                },
                rotation: snapResult.rotation || effectiveDragItem.rotation,
                isDragging: true,
                isSnappedToWall: true,
                _snappedWall: snapResult.wall,
              };
            }
          }
        }

        // Fall back to free drag if no snap occurred
        if (!draggedObject) {
          draggedObject = {
            ...effectiveDragItem,
            center: newCenter,
            isDragging: true,
            isSnappedToWall: false,
            _snappedWall: null,
          };
        }

        // Corner tub auto-rotation: determine which quadrant of the
        // room the tub is in and rotate accordingly. Matches the debug
        // page approach (TubSurroundDebug.js CORNER_ROTATIONS).
        const isCornerTubDrag =
          draggedObject.isCornerTub === true &&
          (draggedObject.type || '').toLowerCase() === 'bathtub';
        if (isCornerTubDrag && structureData?.rooms) {
          const tubRoom =
            (draggedObject._roomId
              ? structureData.rooms.find((r) => r.id === draggedObject._roomId)
              : null) ||
            structureData.rooms.find((r) =>
              r.objects?.some((o) => o?.id === draggedObject.id)
            );
          const roomWalls = tubRoom?.walls || [];
          if (roomWalls.length >= 2) {
            // Room center from wall centers
            let sumX = 0,
              sumZ = 0,
              cnt = 0;
            for (const w of roomWalls) {
              if (w.center) {
                sumX += w.center.x;
                sumZ += w.center.z;
                cnt++;
              }
            }
            if (cnt > 0) {
              const roomCx = sumX / cnt;
              const roomCz = sumZ / cnt;
              const cx = draggedObject.center.x;
              const cz = draggedObject.center.z;
              const isLeft = cx < roomCx;
              const isTop = cz < roomCz;

              // rotation.y = -(debugDegrees * π / 180)
              // topLeft=315°, topRight=45°, bottomLeft=225°, bottomRight=135°
              let rotY;
              if (isLeft && isTop) rotY = -(315 * Math.PI) / 180;
              else if (isLeft && !isTop) rotY = -(225 * Math.PI) / 180;
              else if (!isLeft && isTop) rotY = -(45 * Math.PI) / 180;
              else rotY = -(135 * Math.PI) / 180;

              // Find the two nearest walls to use as parentWallIds
              const wallDists = roomWalls
                .filter((w) => w.center)
                .map((w) => ({
                  wall: w,
                  dist: Math.hypot(cx - w.center.x, cz - w.center.z),
                }))
                .sort((a, b) => a.dist - b.dist);
              const nearestWallIds = wallDists
                .slice(0, 2)
                .map((wd) => wd.wall.id);

              draggedObject = {
                ...draggedObject,
                rotation: {
                  ...(draggedObject.rotation || {}),
                  y: rotY,
                },
                parentWallIds: nearestWallIds,
              };
            }
          }
        }

        // Real-time collision detection + snap during drag
        // Prefer fresh room data from structureData (has disabledConstraints from Firestore)
        const dragRoom =
          (draggedObject._roomId && structureData?.rooms
            ? structureData.rooms.find((r) => r.id === draggedObject._roomId)
            : null) || selectedRoom;
        if (dragRoom && draggedObject.center && draggedObject.dimensions) {
          const dragRoomData = {
            walls: dragRoom.walls || [],
            doors: dragRoom.doors || [],
            windows: dragRoom.windows || [],
            openings: dragRoom.openings || [],
            objects: dragRoom.objects || [],
            floors: dragRoom.floors || [],
          };
          const roomConstraints = dragRoom.disabledConstraints || [];
          const dragOptions = {
            excludeIds: [draggedObject.id],
            excludeGroupIds: draggedObject.fixtureGroupId
              ? [draggedObject.fixtureGroupId]
              : [],
            snappedWall: draggedObject._snappedWall || undefined,
            skipChecks: toSkipChecks(roomConstraints),
            structureDoorsById,
          };
          // Build debug state
          const snappedWall = draggedObject._snappedWall;
          const wallAxis = (() => {
            if (!snappedWall) return null;
            // Prefer start/end (consistent with getWallAxis in snapToValid)
            let s, e;
            if (snappedWall.start && snappedWall.end) {
              s = snappedWall.start;
              e = snappedWall.end;
            } else if (
              snappedWall.alignment?.bottomLeft &&
              snappedWall.alignment?.bottomRight
            ) {
              s = snappedWall.alignment.bottomLeft;
              e = snappedWall.alignment.bottomRight;
            }
            if (!s || !e) return null;
            const dx = e.x - s.x;
            const dz = e.z - s.z;
            const len = Math.sqrt(dx * dx + dz * dz);
            return len > 0 ? { x: dx / len, z: dz / len } : null;
          })();

          const debugState = {
            snappedWall,
            wallDir: wallAxis,
            wallSource: snappedWall
              ? 'snapElementToWall'
              : draggedObject.parentWallIds?.[0]
                ? 'parentWallIds'
                : 'none',
            objectCenter: draggedObject.center,
            objectDimensions: draggedObject.dimensions,
            objectRotation: draggedObject.rotation,
            collisions: [],
            snapResult: null,
            wallClamp: draggedObject.isSnappedToWall ? 'snapped' : 'free',
          };

          const dragValidation = checkPlacement(
            draggedObject,
            dragRoomData,
            dragOptions
          );

          // Filter out collisions for disabled constraint categories
          const fixtureType = (draggedObject.type || '').toLowerCase();
          const filteredCollisions = filterCollisions(
            dragValidation.collisions || [],
            fixtureType,
            roomConstraints
          );
          dragValidation.collisions = filteredCollisions;
          dragValidation.valid = filteredCollisions.length === 0;

          debugState.collisions = filteredCollisions;

          if (!dragValidation.valid) {
            const snap = snapToValidPosition(
              draggedObject,
              dragRoomData,
              dragValidation,
              dragOptions
            );
            debugState.snapResult = snap || { snapped: false };
            debugState.snapDebugLog = snap?.debugLog || [];
            debugState.boundaryVisuals =
              snap?.debugLog?.boundaryVisuals || null;
            if (snap?.snapped) {
              draggedObject = {
                ...draggedObject,
                center: snap.center,
                hasCollision: false,
                // Only update clearanceOffset if the snap explicitly set one,
                // otherwise preserve the existing offset
                ...(snap.clearanceOffset !== undefined
                  ? { clearanceOffset: snap.clearanceOffset }
                  : {}),
              };
              debugState.objectCenter = snap.center;
            } else {
              draggedObject = { ...draggedObject, hasCollision: true };
            }
          } else {
            draggedObject = { ...draggedObject, hasCollision: false };
          }

          collisionDebugRef.current = debugState;
          lastCollisionDebugRef.current = debugState;
        }

        // Store for use at drag-end (selectedItem may be stale due to async state)
        lastDraggedObjectRef.current = draggedObject;

        // Update parent immediately. Publish the canonical drag anchor (the
        // redirected target, e.g. cabinet for a vanity, support-box for a
        // tub) so the cascade in UVFloorPlanPage rotates members around the
        // right rigid-body pivot. Visual selection identity is preserved by
        // the displaySelectedItem trick downstream and by the drop-end
        // publish (which re-publishes the user's original click target).
        if (onItemSelect) {
          onItemSelect(draggedObject);
        }

        // Update cursor for regular objects (closed hand while dragging)
        canvas.style.cursor = 'grabbing';
      }
      return;
    }

    // Handle resizing
    if (
      isResizing &&
      activeResizeHandle &&
      resizeStartPos &&
      originalObjectState
    ) {
      const dragDelta = {
        x: worldCoords.x - resizeStartPos.x,
        z: worldCoords.y - resizeStartPos.z,
      };

      // Calculate new dimensions (corner handles preserve aspect ratio)
      const isCornerHandle =
        activeResizeHandle.includes('corner') ||
        (activeResizeHandle.includes('top') &&
          activeResizeHandle.includes('left')) ||
        (activeResizeHandle.includes('top') &&
          activeResizeHandle.includes('right')) ||
        (activeResizeHandle.includes('bottom') &&
          activeResizeHandle.includes('left')) ||
        (activeResizeHandle.includes('bottom') &&
          activeResizeHandle.includes('right'));

      const resizeResult = calculateResize(
        originalObjectState,
        activeResizeHandle,
        dragDelta,
        isCornerHandle // Preserve aspect ratio for corner handles
      );

      if (resizeResult) {
        const newObject = {
          ...selectedItem,
          center: resizeResult.center,
          dimensions: resizeResult.dimensions,
        };
        setTempResizedObject(newObject);
      } else {
      }
      return;
    }

    // Over a PIN on a fixed cabinet: a click releases it.
    const overPin =
      !!onUnpinCabinet &&
      !isDraggingRef.current &&
      !!fixedPinAt(fixedPinHitRef?.current, {
        x: worldCoords.x,
        z: worldCoords.y,
      });
    if (overPin) {
      canvas.style.cursor = 'pointer';
    }
    // Update cursor based on hover over wall handles
    else if (
      selectedItem &&
      isWall(selectedItem) &&
      !isDraggingRef.current &&
      !isDraggingWall
    ) {
      const clickPoint = { x: worldCoords.x, z: worldCoords.y };
      const wallHandle = hitTestWallHandles(clickPoint, selectedItem, scale, {
        coarse: grabHandlesEnabled,
      });

      if (wallHandle) {
        canvas.style.cursor = getWallHandleCursor(wallHandle, selectedItem);
      } else {
        // Check if hovering over wall body
        if (isPointInObject(clickPoint, selectedItem)) {
          // Show move cursor when hovering over wall body (4-arrow icon)
          canvas.style.cursor = 'move';
        } else {
          canvas.style.cursor = 'default';
        }
      }
    }
    // Update cursor for wall-mounted elements. Skipped in resize mode so
    // the cursor falls through to the resize-handle hit-test branch
    // below — otherwise an opening shows the move cursor over its
    // handles instead of the resize arrows.
    else if (
      isWallMountedOpening(selectedItem) &&
      !isDraggingRef.current &&
      objectInteractionMode === 'move'
    ) {
      const clickPoint = { x: worldCoords.x, z: worldCoords.y };

      // For wall elements, add some margin since they can be very thin
      // Create a temporary object with expanded dimensions for hit testing
      const hitTestItem = {
        ...selectedItem,
        dimensions: {
          x: selectedItem.dimensions.x,
          y: selectedItem.dimensions.y,
          z: Math.max(selectedItem.dimensions.z, 0.3), // Ensure at least 30cm depth for hit testing
        },
      };

      const isHovering =
        isPointInObject(clickPoint, hitTestItem) ||
        (grabHandlesEnabled &&
          hitTestGrabHandle(
            clickPoint,
            selectedItem,
            scale,
            grabRoomCenter(selectedItem)
          ));

      // Debug logging - reset for each element type
      const elementType = selectedItem.category?.door
        ? 'door'
        : selectedItem.category?.window
          ? 'window'
          : selectedItem.category?.opening
            ? 'opening'
            : 'unknown';
      const debugKey = `wallElementHoverDebug_${elementType}`;

      if (isHovering) {
        // An opening only slides along its wall, so the cursor is the two-way
        // arrow at the wall's angle — the four-way `move` read as free
        // movement, which a drag here never was.
        canvas.style.cursor = alongWallCursor(
          apply3DRotationTo2D(selectedItem.rotation)
        );
      } else {
        canvas.style.cursor = isDraggingRef.current ? 'grabbing' : 'grab';
      }
    }
    // Update cursor based on hover over resize handles or object
    else if (
      selectedItem &&
      !selectedItem.isRoomLabel &&
      !isWall(selectedItem) &&
      !isDraggingRef.current
    ) {
      const clickPoint = { x: worldCoords.x, z: worldCoords.y };

      const hoverRunId =
        objectInteractionMode === 'resize'
          ? runDrag?.runIdFor?.(selectedItem)
          : null;
      if (
        runDrag &&
        moveHandleOfferedFor(selectedItem, runDrag, cabinetEditScope) &&
        hitTestGrabHandle(
          clickPoint,
          selectedItem,
          scale,
          grabRoomCenter(selectedItem)
        )
      ) {
        // Over the MOVE handle: it slides the bay along its run, any mode.
        canvas.style.cursor = alongWallCursor(
          apply3DRotationTo2D(selectedItem.rotation)
        );
      } else if (hoverRunId) {
        // Resize on a run-owned object means the RUN's end handles.
        const h = runDrag.hitTestEnd(clickPoint, selectedItem, scale, {
          coarse: grabHandlesEnabled,
        });
        canvas.style.cursor = h
          ? h.pinned
            ? 'not-allowed'
            : alongWallCursor(h.dir)
          : 'grab';
      } else if (objectInteractionMode === 'resize') {
        const handle = hitTestHandles(
          clickPoint,
          selectedItem,
          scale,
          canvasResizeHandlesFor(selectedItem),
          { coarse: grabHandlesEnabled }
        );
        if (handle) {
          const rotation2D = selectedItem.rotation
            ? apply3DRotationTo2D(selectedItem.rotation)
            : [1, 0];
          const angle = Math.atan2(rotation2D[1], rotation2D[0]);
          canvas.style.cursor = getHandleCursor(handle, angle);
        } else {
          canvas.style.cursor = 'grab';
        }
      } else if (
        (objectInteractionMode === 'move' ||
          objectInteractionMode === 'move-group') &&
        isPointInObject(clickPoint, selectedItem)
      ) {
        canvas.style.cursor = 'move';
      } else {
        canvas.style.cursor = isDraggingRef.current ? 'grabbing' : 'grab';
      }
    } else if (placementMode) {
      // Show crosshair cursor when in placement mode
      canvas.style.cursor = 'crosshair';
    } else {
      canvas.style.cursor = isDraggingRef.current ? 'grabbing' : 'grab';
    }

    // Handle panning
    if (!isDraggingRef.current) {
      return;
    }

    // Calculate movement from the last position (using ref for immediate updates)
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;

    // Only update if there's actual movement
    if (dx !== 0 || dy !== 0) {
      // Update pan
      setPan((prevPan) => ({
        x: prevPan.x + dx,
        y: prevPan.y + dy,
      }));

      // Track total movement
      mouseMovementRef.current += Math.abs(dx) + Math.abs(dy);

      // Mark as dragged if movement is significant
      if (mouseMovementRef.current > 5) {
        setHasDraggedMouse(true);
      }

      // Update drag start ref immediately for next move
      dragStartRef.current = { x: e.clientX, y: e.clientY };

      // Reset auto-pan state when user manually pans
      if (autoPanState.hasAutoPanned) {
        setAutoPanState({
          hasAutoPanned: false,
          originalPan: null,
        });
      }
    }
  };

  const handleMouseUp = () => {
    // Finish shaping a wire. A press with no movement commits nothing, so a
    // stray click on the handle cannot flatten a shaped wire.
    if (wireHandleDragRef.current) {
      const { connectionId, shape } = wireHandleDragRef.current;
      wireHandleDragRef.current = null;
      if (shape) onWireShapeCommit?.(connectionId, shape);
      else onWireShapeDrag?.(connectionId, null);
      return;
    }

    // End any in-progress stretch translate-rectangle edge drag.
    if (draggingRegionEdgeRef.current) {
      draggingRegionEdgeRef.current = false;
      return;
    }

    // Handle wall drag completion
    if (isDraggingWall) {
      console.log('🛑 Wall drag completed, clearing state:', {
        isDraggingWall,
        activeWallHandle,
        wallDragStart,
        originalWallState,
        selectedItem: selectedItem?.id,
      });

      // Clear interaction state immediately
      setIsDraggingWall(false);
      setActiveWallHandle(null);
      setWallDragStart(null);
      setOriginalWallState(null);
      setWallRotationDebug(null);

      // Nothing to publish: the edit lives in the stretch plan, which stays in
      // PREVIEW so the panel's Confirm can commit it. Block the click the
      // browser fires next, or the hit test re-selects under the cursor.
      setHasDraggedMouse(true);
      if (onWallLengthDragEnd) onWallLengthDragEnd();
      return;
    }

    // Handle wall element drag completion (doors, windows, openings)
    if (isDraggingWallElement) {
      // Block the click event the browser fires after mouseup —
      // without this, handleClick hit-tests under the cursor and
      // selects the parent wall, overriding the door / window /
      // opening selection. Matches the isDraggingObject branch below.
      setHasDraggedMouse(true);
      // Clear drag interaction state immediately
      setIsDraggingWallElement(false);
      setWallElementDragStart(null);
      setOriginalWallElementPosition(null);

      // Mark element as no longer dragging and trigger save
      if (selectedItem && onItemSelect) {
        const updatedElement = {
          ...selectedItem,
          isDragging: false, // Clear the dragging flag
          isMoved: true, // Mark that it was moved and needs saving
          // Preserve room context and parent wall
          _roomId: selectedItem?._roomId,
          parentWallIds: selectedItem?.parentWallIds,
        };

        console.log('Wall element drag completed, triggering save:', {
          id: updatedElement.id,
          center: updatedElement.center,
          isMoved: updatedElement.isMoved,
          category: updatedElement.category,
        });

        onItemSelect(updatedElement);
      }
      return;
    }

    // Handle object drag completion (including whole wall dragging)
    if (isDraggingRun && runDrag) {
      setHasDraggedMouse(true);
      setIsDraggingRun(false);
      if (appliancePreviewRaf.current != null) {
        cancelAnimationFrame(appliancePreviewRaf.current);
        appliancePreviewRaf.current = null;
      }
      // STICKY ghost: the commit writes `roomsData`, and a mirror effect
      // copies that into `structureData.rooms` one render later — which is
      // what the canvas draws. Clearing the ghost now paints one frame of
      // the pre-drag room (the sink snapped back, then forward — user-seen
      // 2026-08-30, twice). So a successful commit only MARKS the preview;
      // the canvas owner clears it when the mirrored data lands.
      const w = appliancePreviewWorld.current;
      appliancePreviewWorld.current = null;
      if (w && runDrag.commit(w)) {
        setAppliancePreview((p) => (p ? { ...p, committed: true } : null));
      } else {
        runDrag.cancel();
        setAppliancePreview(null);
      }
      return;
    }

    if (isDraggingObject) {
      // Block the click event that fires after mouseup (prevents selecting object under drop point)
      setHasDraggedMouse(true);
      // Clear debug overlay
      collisionDebugRef.current = null;

      // Capture originalObjectPosition before clearing (needed for move detection)
      const savedOriginalPosition = originalObjectPosition;

      // Clear drag interaction state immediately
      setIsDraggingObject(false);
      setObjectDragStart(null);
      setOriginalObjectPosition(null);

      // Mark object/wall as no longer dragging
      // Use lastDraggedObjectRef to get the final snapped position/rotation
      // (selectedItem may be stale due to async React state updates)
      let baseItem =
        lastDraggedObjectRef.current ||
        dragRedirectTargetRef.current ||
        selectedItem;
      lastDraggedObjectRef.current = null;
      dragRedirectTargetRef.current = null;

      // Only mark as moved if the object actually changed position
      // (mousedown+mouseup without mousemove should not trigger a save)
      const actuallyMoved =
        savedOriginalPosition &&
        baseItem?.center &&
        (Math.abs(baseItem.center.x - savedOriginalPosition.x) > 0.0001 ||
          Math.abs(baseItem.center.z - savedOriginalPosition.z) > 0.0001);

      if (baseItem && onItemSelect) {
        // Validate placement after drop (check even if not moved to preserve collision state)
        let hasCollision = false;
        if (baseItem.center && baseItem.dimensions) {
          // Prefer fresh room data from structureData (has disabledConstraints from Firestore)
          const room =
            (baseItem._roomId && structureData?.rooms
              ? structureData.rooms.find((r) => r.id === baseItem._roomId)
              : null) || selectedRoom;
          if (room) {
            const roomData = {
              walls: room.walls || [],
              doors: room.doors || [],
              windows: room.windows || [],
              openings: room.openings || [],
              objects: room.objects || [],
              floors: room.floors || [],
            };
            const dropRoomConstraints = room.disabledConstraints || [];
            const placementOptions = {
              excludeIds: [baseItem.id],
              excludeGroupIds: baseItem.fixtureGroupId
                ? [baseItem.fixtureGroupId]
                : [],
              snappedWall: baseItem._snappedWall || undefined,
              skipChecks: toSkipChecks(dropRoomConstraints),
              structureDoorsById,
            };
            const validation = checkPlacement(
              baseItem,
              roomData,
              placementOptions
            );
            // Filter collisions for disabled per-fixture-type categories
            const itemType = (baseItem.type || '').toLowerCase();
            validation.collisions = filterCollisions(
              validation.collisions || [],
              itemType,
              dropRoomConstraints
            );
            validation.valid = validation.collisions.length === 0;
            if (!validation.valid) {
              // Try snapping to nearest valid position
              const snap = snapToValidPosition(
                baseItem,
                roomData,
                validation,
                placementOptions
              );
              if (snap?.snapped) {
                baseItem = { ...baseItem, center: snap.center };
              } else {
                hasCollision = true;
              }
            }
          }
        }

        // Update parentWallIds to match the snapped wall (or clear if no wall)
        const snappedWallId = baseItem._snappedWall?.id;
        const updatedParentWallIds = snappedWallId ? [snappedWallId] : null;

        const updatedItem = {
          ...baseItem,
          isDragging: false,
          isMoved: actuallyMoved ? true : false,
          hasCollision,
          parentWallIds: updatedParentWallIds,
          // Include original center for calculating child element translations (for walls)
          originalCenter: savedOriginalPosition || baseItem.center,
          // Preserve room context if it exists
          _roomId: baseItem?._roomId,
        };

        onItemSelect(updatedItem);

        // If drag was internally redirected (e.g. tub/vanity group anchored
        // on the support-box/cabinet), re-select the user's original click
        // target so selection identity returns to what they actually clicked.
        // Pure selection update — isMoved/isDragging cleared so save logic
        // doesn't fire again.
        const originalClickTarget = selectedAtMouseDownRef.current;
        if (originalClickTarget && originalClickTarget.id !== updatedItem.id) {
          onItemSelect({
            ...originalClickTarget,
            isMoved: false,
            isDragging: false,
            _roomId: originalClickTarget._roomId || updatedItem._roomId,
          });
        }
      }
      return;
    }

    // Handle resize completion
    if (isResizing && tempResizedObject) {
      setHasDraggedMouse(true);
      // Clear resize interaction state immediately
      setIsResizing(false);
      setActiveResizeHandle(null);
      setResizeStartPos(null);
      setOriginalObjectState(null);

      // Notify parent component about the resize
      if (onItemSelect) {
        // Validate resized placement
        let hasCollision = false;
        const resizeRoom =
          selectedRoom ||
          (tempResizedObject._roomId && structureData?.rooms
            ? structureData.rooms.find(
                (r) => r.id === tempResizedObject._roomId
              )
            : null);
        if (
          resizeRoom &&
          tempResizedObject.center &&
          tempResizedObject.dimensions
        ) {
          const roomData = {
            walls: resizeRoom.walls || [],
            doors: resizeRoom.doors || [],
            windows: resizeRoom.windows || [],
            openings: resizeRoom.openings || [],
            objects: resizeRoom.objects || [],
            floors: resizeRoom.floors || [],
          };
          const validation = checkPlacement(tempResizedObject, roomData, {
            excludeIds: [tempResizedObject.id],
            excludeGroupIds: tempResizedObject.fixtureGroupId
              ? [tempResizedObject.fixtureGroupId]
              : [],
            structureDoorsById,
          });
          if (!validation.valid) {
            console.warn(
              `[FloorPlan] Resize collision:`,
              validation.collisions.map((c) => c.reason)
            );
            hasCollision = true;
          }
        }

        const updatedObject = {
          ...tempResizedObject,
          isResized: true,
          hasCollision,
          // Preserve room context if it exists
          _roomId: selectedItem._roomId,
        };

        // Keep the temp object visible until parent updates
        // The cleanup effect will clear it after parent has processed
        onItemSelect(updatedObject);
      }
      return;
    }

    setIsDragging(false);
    isDraggingRef.current = false;

    // Notify parent of final pan position using ref to get current value
    if (onPanChange) {
      onPanChange(panRef.current);
    }

    // Reset drag flag with small delay
    setTimeout(() => {
      setHasDraggedMouse(false);
      mouseMovementRef.current = 0;
    }, 50);
  };

  const handleMouseLeave = () => {
    draggingRegionEdgeRef.current = false;
    setIsDragging(false);
    isDraggingRef.current = false;

    // Notify parent of final pan position using ref to get current value
    if (onPanChange) {
      onPanChange(panRef.current);
    }

    setTimeout(() => {
      setHasDraggedMouse(false);
      mouseMovementRef.current = 0;
    }, 50);
  };

  // Handle mouse wheel / trackpad pinch for zooming
  const handleWheel = useCallback((e) => {
    e.preventDefault();

    // Use ctrlKey to detect trackpad pinch gestures (browsers set ctrlKey for pinch-to-zoom)
    // For pinch: use -deltaY directly for smooth scaling
    // For scroll wheel: use sign-based delta for consistent steps
    const isPinch = e.ctrlKey;
    if (!isPinch && e.deltaY === 0) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const zoomDpr = window.devicePixelRatio || 1;
    const centerX = canvas.width / zoomDpr / 2;
    const centerY = canvas.height / zoomDpr / 2;

    // Read current values from refs for immediate accuracy
    const prevScale = scaleRef.current;
    const prevPan = panRef.current;

    let zoomFactor;
    if (isPinch) {
      // Trackpad pinch: use continuous delta for smooth zoom
      zoomFactor = 1 - e.deltaY * 0.01;
    } else {
      // Scroll wheel: use sign-based 3% steps
      const delta = e.deltaY > 0 ? -1 : 1;
      zoomFactor = 1 + delta * 0.03;
    }
    const newScale = Math.min(1000, Math.max(10, prevScale * zoomFactor));

    if (newScale === prevScale) return;

    const scaleChange = newScale / prevScale;
    const mouseRelX = mouseX - centerX - prevPan.x;
    const mouseRelY = mouseY - centerY - prevPan.y;
    const newPan = {
      x: prevPan.x + mouseRelX * (1 - scaleChange),
      y: prevPan.y + mouseRelY * (1 - scaleChange),
    };

    // Update refs immediately so next event reads correct values
    scaleRef.current = newScale;
    panRef.current = newPan;

    setScale(newScale);
    setPan(newPan);
  }, []);

  // Add wheel event listener on container (not canvas) so it captures
  // events over overlay panels (legend, details, scale indicator, etc.)
  // Uses a callback ref to ensure the listener is attached when the DOM mounts.
  const wheelHandlerRef = useRef(null);
  wheelHandlerRef.current = handleWheel;
  const wheelCleanupRef = useRef(null);

  const containerWheelRef = useCallback((node) => {
    if (wheelCleanupRef.current) {
      wheelCleanupRef.current();
      wheelCleanupRef.current = null;
    }

    if (node) {
      const handler = (e) => wheelHandlerRef.current(e);
      node.addEventListener('wheel', handler, { passive: false });
      wheelCleanupRef.current = () =>
        node.removeEventListener('wheel', handler);
    }
  }, []);

  // Track what was selected at mouseDown time to prevent immediate dragging
  const lastSelectionTimeRef = useRef(0);

  // Track overlapping item cycle state for click-to-cycle feature
  const overlapCycleRef = useRef({ items: [], index: 0 });

  // Track when selection changes
  // The room a selected element belongs to, for the grab handle's "into
  // the room" side. Read through a ref by the touch listeners, which are
  // registered once.
  const grabRoomCenter = (item) =>
    roomCentroid(
      selectedRoom ||
        (item?._roomId
          ? structureData?.rooms?.find((r) => r.id === item._roomId)
          : null)
    );
  // Move Group's closure — every object that travels with the selected one
  // — so a finger on ANY member starts the group drag, as a mouse does.
  const groupMembersOf = (item) => {
    if (!item?.id) return [];
    for (const room of structureData?.rooms || []) {
      if (!room.objects?.some((o) => o?.id === item.id)) continue;
      const group = getFreeMoveGroup(item.id, room);
      return room.objects.filter((o) => o && group.has(o.id));
    }
    return [];
  };
  const grabContextRef = useRef({
    grabHandlesEnabled,
    grabRoomCenter,
    groupMembersOf,
    canvasResizeHandlesFor,
    runDrag,
  });
  useEffect(() => {
    grabContextRef.current = {
      grabHandlesEnabled,
      grabRoomCenter,
      groupMembersOf,
      canvasResizeHandlesFor,
      // The touch hit-test must ask the same questions the mouse path does
      // about cabinet runs, or a finger on a run handle falls through to
      // "nothing here" and pans the plan instead — see touchDragTarget.
      runDrag,
    };
  });
  const prevSelectedRef = useRef(null);
  // The touch listeners are registered once and read the mode through a
  // ref, as they do the selection — otherwise a finger on a selected
  // element is captured (no pan) by a drag test the mouse path then
  // refuses, and the finger does nothing at all.
  const interactionModeRef = useRef(objectInteractionMode);
  // The touch path reads at EVENT time, not at setup time — same reason the
  // interaction mode is held in a ref beside it.
  const cabinetScopeRef = useRef(cabinetEditScope);
  useEffect(() => {
    interactionModeRef.current = objectInteractionMode;
    cabinetScopeRef.current = cabinetEditScope;
  }, [objectInteractionMode, cabinetEditScope]);
  useEffect(() => {
    if (selectedItem?.id !== prevSelectedRef.current?.id) {
      lastSelectionTimeRef.current = Date.now();
    }
    // Always update the ref so touch hit-testing uses the latest
    // position (e.g., after a drag moves the object)
    prevSelectedRef.current = selectedItem;
  }, [selectedItem]);

  // Mouse move is now handled directly by panHandlers

  // Use refs to store current values for touch handlers
  const touchStateRef = useRef({
    isPanning: false,
    lastTouchPos: null,
    initialDistance: 0,
    initialScale: DEFAULT_SCALE, // Use default scale value (will be updated)
    currentScale: DEFAULT_SCALE, // Initialize currentScale (will be updated)
    hasMoved: false, // Track if user has moved during touch
    wasPinching: false, // Track if we were pinching to prevent click after zoom
    blockNextClick: false, // Block the next click event after pinch
    // Pending drag: touch landed on interactive element but drag hasn't
    // started yet (waiting for movement to exceed threshold)
    pendingDrag: null, // { touch, event, startX, startY } or null
    isInteractingWithElement: false, // Touch started on an interactive element (drag/resize)
    // Drag space (see touchDragSpace.js): the promotion offset every
    // dispatched position is pulled back by, and the lift a MOVE rides at.
    dragOffset: null, // { x, y } or null
    lift: false, // does the dragged object ride above the fingertip?
  });

  // Update the scale ref when scale changes
  useEffect(() => {
    touchStateRef.current.currentScale = scale;
  }, [scale]);

  // Set up non-passive touch event listeners to allow preventDefault
  useEffect(() => {
    if (readOnly) return;
    let touchStartHandler, touchMoveHandler, touchEndHandler;
    let gestureStartHandler, gestureChangeHandler, gestureEndHandler;

    // Add a small delay to ensure canvas is rendered
    const setupTimeout = setTimeout(() => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) {
        return;
      }

      const getTouchDistance = (touches) => {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
      };

      // Helper: create a synthetic MouseEvent from a Touch object
      // Where a live touch point sits in the drag's own space: pulled back
      // by the promotion offset, and lifted if this drag is lifted. Both
      // are offsets of the whole drag rather than functions of the pointer
      // — see touchDragSpace.js for why that distinction is load-bearing.
      const dragSpace = (touch) => toDragSpace(touch, touchStateRef.current);
      const touchToMouseEvent = (type, touch, originalEvent) => {
        return new MouseEvent(type, {
          bubbles: true,
          cancelable: true,
          clientX: touch.clientX,
          clientY: touch.clientY,
          screenX: touch.screenX,
          screenY: touch.screenY,
          view: window,
          // Pass through modifier keys if available
          ctrlKey: originalEvent.ctrlKey || false,
          shiftKey: originalEvent.shiftKey || false,
          altKey: originalEvent.altKey || false,
          metaKey: originalEvent.metaKey || false,
        });
      };

      // Helper: WHAT a touch point lands on, of the things a drag can start
      // from — `'grab-handle'`, `'body'` or `'handle'` (a resize / wall-end
      // grip), else null for "nothing, so pan". It answers with the target
      // rather than a bare yes because the LIFT depends on it: a drag begun
      // on the grab handle is already clear of the object it moves, and
      // lifting it too would tear the object off the tether drawn between
      // them. Wrapped in try-catch because this runs on every touchstart and
      // must not crash — a null return just falls back to panning.
      const touchDragTarget = (touch) => {
        try {
          const currentSelected = prevSelectedRef.current;
          if (
            !currentSelected ||
            !currentSelected.center ||
            !currentSelected.dimensions
          ) {
            return null;
          }

          const rect = canvas.getBoundingClientRect();
          const screenX = touch.clientX - rect.left;
          const screenY = touch.clientY - rect.top;
          const currentPan = panRef.current;
          const currentScale = scaleRef.current;
          const worldCoords = screenToWorldCoordinates(
            screenX,
            screenY,
            canvas,
            currentPan,
            currentScale,
            rotation
          );
          if (!worldCoords) return null;
          const clickPoint = { x: worldCoords.x, z: worldCoords.y };

          // Only claim the finger for what the mouse path would actually
          // do with it: a body drag needs Move armed, a handle drag needs
          // Resize. Anything else is a pan.
          const mode = interactionModeRef.current;
          const canDragBody = mode === 'move' || mode === 'move-group';
          const canDragHandles = mode === 'resize';
          const grab = grabContextRef.current;

          // CABINET RUNS, before anything generic AND before the mode gate: a
          // run's handles sit wherever the RUN is, not where the selected
          // cabinet is, and since 2026-10-03 they are drawn as soon as a run
          // object is selected — so a finger has to be able to claim one with
          // nothing armed, or touch and mouse disagree about a handle that is
          // plainly on screen. Returns 'handle' rather than 'body'
          // deliberately: that is what keeps the touch LIFT off, and a run drag
          // reads the pointer's exact position (a delta from the press, or a
          // param along the wall), which a lift would shift.
          const runTarget = runPointerTarget({
            point: clickPoint,
            selected: currentSelected,
            scale: currentScale,
            coarse: grab.grabHandlesEnabled,
            runDrag: grab.runDrag,
            mode,
            scope: cabinetScopeRef.current,
            hitMoveHandle: (p) =>
              hitTestGrabHandle(
                p,
                currentSelected,
                currentScale,
                grab.grabRoomCenter(currentSelected)
              ),
          });
          if (runTarget === 'handle') return 'handle';
          if (!canDragBody && !canDragHandles) return null;
          if (runTarget === null) return null;

          if (
            canDragBody &&
            grab.grabHandlesEnabled &&
            // Asked through the same predicate the renderer draws it with,
            // so a finger can never land on a handle that is not there.
            grabHandleOfferedFor(currentSelected, grab.runDrag) &&
            hitTestGrabHandle(
              clickPoint,
              currentSelected,
              currentScale,
              grab.grabRoomCenter(currentSelected)
            )
          ) {
            return 'grab-handle';
          }

          // Check selected wall handles and body
          if (isWall(currentSelected)) {
            const wallHandle = hitTestWallHandles(
              clickPoint,
              currentSelected,
              currentScale,
              { coarse: grab.grabHandlesEnabled }
            );
            if (wallHandle && canDragHandles) return 'handle';
            if (canDragBody && isPointInObject(clickPoint, currentSelected))
              return 'body';
          }

          // Check wall-mounted element (door/window/opening)
          if (
            (currentSelected.category?.door ||
              currentSelected.category?.window ||
              currentSelected.category?.opening) &&
            currentSelected.parentWallIds?.[0]
          ) {
            const hitTestItem = {
              ...currentSelected,
              dimensions: {
                x: currentSelected.dimensions.x,
                y: currentSelected.dimensions.y,
                z: Math.max(currentSelected.dimensions.z, 0.3),
              },
            };
            if (canDragBody && isPointInObject(clickPoint, hitTestItem))
              return 'body';
            // Resize: only the opening's own end handles (the two the mouse
            // path offers) claim the finger — claiming it anywhere left the
            // plan un-pannable while an opening was selected in Resize.
            if (
              canDragHandles &&
              hitTestHandles(
                clickPoint,
                currentSelected,
                currentScale,
                grab.canvasResizeHandlesFor?.(currentSelected) ?? null,
                { coarse: grab.grabHandlesEnabled }
              )
            ) {
              return 'handle';
            }
            return null;
          }

          // Check selected object resize handles and body
          if (
            !currentSelected.isRoomLabel &&
            !isWall(currentSelected) &&
            !(
              (currentSelected.category?.door ||
                currentSelected.category?.window ||
                currentSelected.category?.opening) &&
              currentSelected.parentWallIds?.[0]
            )
          ) {
            const handle = hitTestHandles(
              clickPoint,
              currentSelected,
              currentScale,
              grab.canvasResizeHandlesFor?.(currentSelected) ?? null,
              { coarse: grab.grabHandlesEnabled }
            );
            if (handle && canDragHandles) return 'handle';
            if (!canDragBody) return null;
            // Move Group: any member of the closure is a grab point, as in
            // the mouse path's isClickingInsideObject expansion.
            if (mode === 'move-group') {
              const members = grab.groupMembersOf(currentSelected);
              if (members.some((o) => isPointInObject(clickPoint, o))) {
                return 'body';
              }
            }
            const isSinkType =
              currentSelected.type && currentSelected.type.includes('sink');
            const isInside = isSinkType
              ? isPointInSinkBounds(clickPoint.x, clickPoint.z, currentSelected)
              : isPointInObject(clickPoint, currentSelected);
            if (isInside) return 'body';
          }

          return null;
        } catch {
          // If anything goes wrong, fall back to panning
          return null;
        }
      };

      // Minimum distance (in pixels) a finger must move before a drag starts
      // on an interactive element. Prevents accidental drags when tapping.
      const TOUCH_DRAG_THRESHOLD = 10;

      touchStartHandler = (e) => {
        touchStateRef.current.hasMoved = false; // Reset movement flag
        touchStateRef.current.isInteractingWithElement = false;
        touchStateRef.current.pendingDrag = null;
        // A new gesture starts in an untransformed drag space; promotion
        // fills these in again.
        touchStateRef.current.dragOffset = null;
        touchStateRef.current.lift = false;

        if (e.touches.length === 1) {
          const touch = e.touches[0];

          // Check if touch lands on a selected element's drag/resize target
          const target = touchDragTarget(touch);
          if (target) {
            // Don't dispatch mousedown yet — wait for movement to exceed
            // TOUCH_DRAG_THRESHOLD before starting a drag. This prevents
            // accidental drags/resizes when the user is just tapping.
            e.preventDefault();
            touchStateRef.current.pendingDrag = {
              startX: touch.clientX,
              startY: touch.clientY,
              target,
              touch: touch,
              event: e,
            };
            touchStateRef.current.lastTouchPos = {
              x: touch.clientX,
              y: touch.clientY,
            };
          } else {
            // No interactive element — default to panning
            touchStateRef.current.isPanning = true;
            touchStateRef.current.lastTouchPos = {
              x: touch.clientX,
              y: touch.clientY,
            };
            setIsDragging(true);
            isDraggingRef.current = true;
            setDragStart({ x: touch.clientX, y: touch.clientY });
          }
        } else if (e.touches.length === 2) {
          // Two touches - prepare for pinch
          // Cancel pending drag if any
          touchStateRef.current.pendingDrag = null;
          // If we were interacting with an element, cancel it
          if (touchStateRef.current.isInteractingWithElement) {
            if (canvasRef.current) {
              canvasRef.current.dispatchEvent(
                new MouseEvent('mouseup', { bubbles: true })
              );
            }
            touchStateRef.current.isInteractingWithElement = false;
          }
          touchStateRef.current.isPanning = false;
          touchStateRef.current.wasPinching = true; // Mark that we're pinching
          touchStateRef.current.initialDistance = getTouchDistance(e.touches);
          touchStateRef.current.initialScale =
            touchStateRef.current.currentScale || scale;
          setIsDragging(false);
          isDraggingRef.current = false;
        }

        // Only prevent default for multi-touch (pinch zoom)
        if (e.touches.length > 1) {
          e.preventDefault();
        }
      };

      touchMoveHandler = (e) => {
        const state = touchStateRef.current;

        // Pending drag: check if movement exceeds threshold to start drag
        if (
          e.touches.length === 1 &&
          state.pendingDrag &&
          !state.isInteractingWithElement
        ) {
          const touch = e.touches[0];
          const dx = touch.clientX - state.pendingDrag.startX;
          const dy = touch.clientY - state.pendingDrag.startY;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance >= TOUCH_DRAG_THRESHOLD) {
            // Threshold exceeded — promote to real drag
            e.preventDefault();
            // The mousedown goes back to where the finger FIRST TOUCHED,
            // because that is the point whose hit-test claimed the finger
            // (touchDragTarget, on touchstart). Sending it from
            // where the finger is NOW made the mouse path re-run its own
            // hit-test up to TOUCH_DRAG_THRESHOLD px away — and a handle's
            // hit radius is only 26 px around a 20 px disc, so a touch on
            // the outer ring of a handle passed the first test and failed
            // the second. That miss falls through handleMouseDown to
            // "otherwise start panning", so the drag silently became a pan
            // of the whole plan and the user tried again ("it takes two
            // touches"). The threshold distance is handed to the drag space
            // as a constant offset instead, so nothing jolts.
            const dragStartX = state.pendingDrag.startX;
            const dragStartY = state.pendingDrag.startY;
            // Read before pendingDrag is cleared below — `state` IS the ref.
            const dragTarget = state.pendingDrag.target;
            touchStateRef.current.dragOffset = { x: dx, y: dy };
            touchStateRef.current.isInteractingWithElement = true;
            touchStateRef.current.hasMoved = true;
            touchStateRef.current.pendingDrag = null;
            // The lift: a FREE object dragged BY ITS BODY rides above the
            // fingertip, because there the finger is on top of the thing it
            // is moving. Everything else is excluded, and each for its own
            // reason:
            //   - the GRAB HANDLE is already offset clear of the object, and
            //     the renderer draws a tether between the two — lifting as
            //     well pulls the object off that tether and (dragging down)
            //     sends it the opposite way for the first frames;
            //   - a wall-mounted element cannot leave its wall, so it stays
            //     put and the renderer shows a readout instead;
            //   - a run-owned appliance's bay drag reads the pointer's
            //     position ALONG the wall, and a screen-vertical lift on a
            //     screen-vertical wall would shift that by the lift;
            //   - a RESIZE reads the pointer's delta as the edge's travel,
            //     so lifting one grew the object's depth while the finger
            //     dragged a side handle sideways.
            // It applies WHOLE, from the first dispatched frame. It used to
            // ramp in, and a ramp is by definition motion the finger did not
            // ask for: over its 180 ms it out-ran a slow downward drag, so
            // the object set off UPWARD before turning to follow.
            const mode = interactionModeRef.current;
            const sel = prevSelectedRef.current;
            touchStateRef.current.lift =
              dragTarget === 'body' &&
              (mode === 'move' || mode === 'move-group') &&
              !!sel &&
              !isWall(sel) &&
              !isWallMountedOpening(sel) &&
              !sel.runId;
            setDragPointer?.('touch');

            // Now dispatch mousedown at the original touch position to start drag
            if (canvasRef.current) {
              const syntheticTouch = {
                clientX: dragStartX,
                clientY: dragStartY,
                screenX: dragStartX,
                screenY: dragStartY,
              };
              canvasRef.current.dispatchEvent(
                touchToMouseEvent('mousedown', syntheticTouch, e)
              );
              // Then immediately dispatch mousemove at the current position.
              // In drag space that IS the mousedown point, so this frame
              // moves nothing — it only primes the mouse path.
              canvasRef.current.dispatchEvent(
                touchToMouseEvent('mousemove', dragSpace(touch), e)
              );
            }

            touchStateRef.current.lastTouchPos = {
              x: touch.clientX,
              y: touch.clientY,
            };
          } else {
            // Below threshold — prevent default but don't start drag yet
            e.preventDefault();
          }
          return;
        }

        // Element interaction mode — route through handleMouseMove
        if (
          e.touches.length === 1 &&
          state.isInteractingWithElement &&
          state.lastTouchPos
        ) {
          e.preventDefault();
          const touch = e.touches[0];
          const dx = touch.clientX - state.lastTouchPos.x;
          const dy = touch.clientY - state.lastTouchPos.y;

          if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
            touchStateRef.current.hasMoved = true;
          }

          // Dispatch mousemove so handleMouseMove handles drag/resize logic
          if (canvasRef.current) {
            canvasRef.current.dispatchEvent(
              touchToMouseEvent('mousemove', dragSpace(touch), e)
            );
          }

          touchStateRef.current.lastTouchPos = {
            x: touch.clientX,
            y: touch.clientY,
          };
          return;
        }

        if (e.touches.length === 1 && state.isPanning && state.lastTouchPos) {
          e.preventDefault(); // Prevent default when we're actually panning

          // Single touch pan
          const touch = e.touches[0];
          const dx = touch.clientX - state.lastTouchPos.x;
          const dy = touch.clientY - state.lastTouchPos.y;

          // Mark as moved if movement is significant (more than 2 pixels)
          if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
            touchStateRef.current.hasMoved = true;
          }

          setPan((prevPan) => ({
            x: prevPan.x + dx,
            y: prevPan.y + dy,
          }));

          touchStateRef.current.lastTouchPos = {
            x: touch.clientX,
            y: touch.clientY,
          };
        } else if (
          e.touches.length === 2 &&
          !state.isPanning &&
          state.initialDistance
        ) {
          e.preventDefault(); // Prevent default for pinch zoom

          // Two finger pinch
          const currentDistance = getTouchDistance(e.touches);
          const distanceRatio = currentDistance / state.initialDistance;

          // Apply a damping factor to make zoom less sensitive
          const dampingFactor = 0.3;
          const scaleFactor = 1 + (distanceRatio - 1) * dampingFactor;

          setScale(() => {
            const newScale = state.initialScale * scaleFactor;
            return Math.min(1000, Math.max(10, newScale));
          });
        }
      };

      touchEndHandler = (e) => {
        setDragPointer?.(null);
        const state = touchStateRef.current;
        // The closing mouseup must land where the last mousemove did, so its
        // position is mapped BEFORE the drag space is torn down.
        const endPos =
          e.changedTouches.length === 1 ? dragSpace(e.changedTouches[0]) : null;
        touchStateRef.current.lift = false;
        touchStateRef.current.dragOffset = null;

        if (e.touches.length === 0) {
          // All touches ended

          // If we had a pending drag that never exceeded the threshold,
          // treat it as a tap — dispatch a click for cycling / selection
          if (state.pendingDrag && !state.isInteractingWithElement) {
            const pd = state.pendingDrag;
            touchStateRef.current.pendingDrag = null;
            touchStateRef.current.isPanning = false;
            touchStateRef.current.lastTouchPos = null;
            touchStateRef.current.hasMoved = false;

            setTimeout(() => {
              if (!canvasRef.current) return;
              const syntheticTouch = {
                clientX: pd.startX,
                clientY: pd.startY,
                screenX: pd.startX,
                screenY: pd.startY,
              };
              canvasRef.current.dispatchEvent(
                touchToMouseEvent('click', syntheticTouch, e)
              );
              touchStateRef.current.blockNextClick = true;
              setTimeout(() => {
                touchStateRef.current.blockNextClick = false;
              }, 400);
            }, 50);
            return;
          }

          // If we were interacting with an element, dispatch mouseup
          if (state.isInteractingWithElement) {
            const didMove = state.hasMoved;
            if (canvasRef.current) {
              if (e.changedTouches.length === 1) {
                const touch = e.changedTouches[0];
                canvasRef.current.dispatchEvent(
                  touchToMouseEvent('mouseup', endPos || touch, e)
                );
                // If no movement occurred, also dispatch a click for
                // click-to-cycle and other click-based interactions
                if (!didMove) {
                  setTimeout(() => {
                    if (!canvasRef.current) return;
                    canvasRef.current.dispatchEvent(
                      touchToMouseEvent('click', touch, e)
                    );
                    // Block native click
                    touchStateRef.current.blockNextClick = true;
                    setTimeout(() => {
                      touchStateRef.current.blockNextClick = false;
                    }, 400);
                  }, 50);
                }
              } else {
                canvasRef.current.dispatchEvent(
                  new MouseEvent('mouseup', { bubbles: true })
                );
              }
            }
            touchStateRef.current.isInteractingWithElement = false;
            touchStateRef.current.isPanning = false;
            touchStateRef.current.lastTouchPos = null;
            touchStateRef.current.hasMoved = false;
            touchStateRef.current.wasPinching = false;
            return;
          }

          // If we were pinching, block the next click
          if (state.wasPinching) {
            touchStateRef.current.blockNextClick = true;
            // Set a timeout to reset the flag after a short delay
            setTimeout(() => {
              touchStateRef.current.blockNextClick = false;
            }, 300);
          }

          // If it was a tap (no movement and not after pinching), simulate click
          if (
            !state.hasMoved &&
            !state.wasPinching &&
            e.changedTouches.length === 1
          ) {
            // Simulate click for tap with a small delay to ensure state is settled
            const touch = e.changedTouches[0];

            setTimeout(() => {
              // Guard: canvas may have been unmounted during the delay
              if (!canvasRef.current) return;

              const clickEvent = new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                clientX: touch.clientX,
                clientY: touch.clientY,
                screenX: touch.screenX,
                screenY: touch.screenY,
                view: window,
              });

              // Dispatch on the canvas element directly
              canvasRef.current.dispatchEvent(clickEvent);

              // Block the browser's native click event that follows touchend
              // (~300ms later) to prevent double-firing
              touchStateRef.current.blockNextClick = true;
              setTimeout(() => {
                touchStateRef.current.blockNextClick = false;
              }, 400);
            }, 50); // Small delay to let touch state settle
          }

          touchStateRef.current.isPanning = false;
          touchStateRef.current.lastTouchPos = null;
          touchStateRef.current.initialDistance = 0;
          touchStateRef.current.hasMoved = false;
          touchStateRef.current.wasPinching = false; // Reset pinching flag
          setIsDragging(false);
          isDraggingRef.current = false;
        } else if (e.touches.length === 1) {
          // One touch remains after pinch
          if (state.isInteractingWithElement) {
            // Cancel element interaction when switching to multi-touch
            if (canvasRef.current) {
              canvasRef.current.dispatchEvent(
                new MouseEvent('mouseup', { bubbles: true })
              );
            }
            touchStateRef.current.isInteractingWithElement = false;
          }
          if (state.wasPinching) {
            // Block click and reset
            touchStateRef.current.blockNextClick = true;
            touchStateRef.current.wasPinching = false;
            touchStateRef.current.isPanning = false;
            // Set a timeout to reset the flag
            setTimeout(() => {
              touchStateRef.current.blockNextClick = false;
            }, 300);
          } else {
            // Normal single touch after another single touch
            touchStateRef.current.isPanning = true;
            touchStateRef.current.lastTouchPos = {
              x: e.touches[0].clientX,
              y: e.touches[0].clientY,
            };
            setIsDragging(true);
            isDraggingRef.current = true;
          }
        }
      };

      // iOS Safari gesture events for pinch zoom (as fallback)
      let gestureScale = 1;
      gestureStartHandler = (e) => {
        e.preventDefault();
        gestureScale = 1;
      };

      gestureChangeHandler = (e) => {
        e.preventDefault();
        // Apply damping to iOS gesture events too
        const dampingFactor = 0.5;
        const scaleDelta = 1 + (e.scale - gestureScale) * dampingFactor;

        setScale((prevScale) => {
          const newScale = prevScale * scaleDelta;
          return Math.min(1000, Math.max(10, newScale));
        });
        gestureScale = e.scale;
      };

      gestureEndHandler = (e) => {
        e.preventDefault();
      };

      // Add all event listeners to canvas
      canvas.addEventListener('touchstart', touchStartHandler, {
        passive: false,
        capture: true, // Try capturing phase
      });
      canvas.addEventListener('touchmove', touchMoveHandler, {
        passive: false,
        capture: true,
      });
      canvas.addEventListener('touchend', touchEndHandler, {
        passive: false,
        capture: true,
      });
      canvas.addEventListener('touchcancel', touchEndHandler, {
        passive: false,
        capture: true,
      });

      // iOS Safari gesture events
      canvas.addEventListener('gesturestart', gestureStartHandler, {
        passive: false,
      });
      canvas.addEventListener('gesturechange', gestureChangeHandler, {
        passive: false,
      });
      canvas.addEventListener('gestureend', gestureEndHandler, {
        passive: false,
      });
    }, 100); // 100ms delay

    return () => {
      clearTimeout(setupTimeout);
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.removeEventListener('touchstart', touchStartHandler, true);
        canvas.removeEventListener('touchmove', touchMoveHandler, true);
        canvas.removeEventListener('touchend', touchEndHandler, true);
        canvas.removeEventListener('touchcancel', touchEndHandler, true);
        canvas.removeEventListener('gesturestart', gestureStartHandler);
        canvas.removeEventListener('gesturechange', gestureChangeHandler);
        canvas.removeEventListener('gestureend', gestureEndHandler);
      }
    };
  }, [setPan, setScale, setIsDragging, setDragStart]); // Only include state setters

  // Handle canvas click to select items or place objects
  const handleClick = useCallback(
    (e) => {
      // Block click if we just finished pinching
      if (touchStateRef.current.blockNextClick) {
        touchStateRef.current.blockNextClick = false;
        return;
      }

      // Block the click that the browser fires after a boundary drag —
      // keeps it from selecting the wall under the mouse and clearing
      // the measurement panel.
      if (suppressNextClickRef.current) {
        suppressNextClickRef.current = false;
        return;
      }

      // Block click if we just finished dragging with mouse
      if (hasDraggedMouse) {
        return;
      }

      // First, get world coordinates for the click
      const rect = canvasRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const worldCoords = screenToWorldCoordinates(
        mouseX,
        mouseY,
        canvasRef.current,
        pan,
        scale,
        rotation
      );

      // Handle vertex move mode — pick up or place vertex on click
      if (vertexMove?.isActive) {
        vertexMove.handleClick({
          x: worldCoords.x,
          z: worldCoords.y,
          shiftHeld: e.shiftKey,
        });
        return;
      }

      // Handle vertex add mode — insert or place vertex on click
      if (vertexAdd?.isActive) {
        vertexAdd.handleClick({
          x: worldCoords.x,
          z: worldCoords.y,
          shiftHeld: e.shiftKey,
        });
        return;
      }

      // Handle vertex delete mode — delete vertex on click
      if (vertexDelete?.isActive) {
        vertexDelete.deleteVertex({ x: worldCoords.x, z: worldCoords.y });
        return;
      }

      // Handle room drawing mode — add vertex on click
      if (placementMode?.type === 'room' && roomDrawing?.isActive) {
        roomDrawing.addVertex(
          { x: worldCoords.x, z: worldCoords.y },
          e.shiftKey,
          scale
        );
        return;
      }

      // Handle wall-chain drawing — every click is a tap; a tap that lands on
      // a wall ends the chain (the hook decides).
      if (placementMode?.type === 'wallChain' && wallChainDrawing?.isActive) {
        wallChainDrawing.addTap(
          { x: worldCoords.x, z: worldCoords.y },
          { shiftHeld: e.shiftKey }
        );
        return;
      }

      // Handle recessed-row drawing — first click sets the start, second
      // commits the row and re-arms for the next one.
      if (
        placementMode?.type === 'recessedRow' &&
        recessedRowDrawing?.isActive
      ) {
        recessedRowDrawing.addVertex({ x: worldCoords.x, z: worldCoords.y });
        return;
      }

      // Handle callout placement — first click is what the note points at,
      // the second is where the cloud hangs and opens the text editor.
      if (placementMode?.type === 'callout' && calloutDrawing?.isActive) {
        calloutDrawing.addVertex({ x: worldCoords.x, z: worldCoords.y });
        return;
      }

      // Handle cabinet-run drawing mode — add a point on click
      if (placementMode?.type === 'cabinetRun' && cabinetRunDrawing?.isActive) {
        // The 2nd click of a double-click (detail >= 2) is the finish gesture,
        // not a new point — ignore it so it never drops a stray vertex. The
        // onDoubleClick handler closes the run.
        if (e.detail >= 2) return;
        cabinetRunDrawing.addVertex(
          { x: worldCoords.x, z: worldCoords.y },
          e.shiftKey
        );
        return;
      }

      // Handle cabinet-run appliance-drop mode — drop the appliance on a run
      if (
        placementMode?.type === 'cabinetAppliance' &&
        cabinetApplianceDrop?.isActive
      ) {
        cabinetApplianceDrop.placeAt({ x: worldCoords.x, z: worldCoords.y });
        return;
      }

      // Handle connection mode - find clicked switch or light
      if (connectionMode && onConnectionSelect) {
        // Collect all objects from all rooms
        const allObjects = [];
        if (structureData?.rooms) {
          structureData.rooms.forEach((room) => {
            if (room.objects) {
              room.objects.forEach((obj) => {
                if (obj && obj.center) {
                  allObjects.push({ ...obj, roomId: room.id });
                }
              });
            }
          });
        }

        // Find clicked object (switch or light). Aim at the SYMBOL, not the
        // hardware: a switch plate is 7 x 4 cm, which is a 5 x 3 px target at
        // plan zoom, while the glyph standing in for it is 16 px across and
        // offset off the wall on a stem. Nearest wireable thing wins — see
        // pickElectricalTargetAt for why both halves of that matter.
        const clickedObject = pickElectricalTargetAt({
          point: { x: worldCoords.x, z: worldCoords.y },
          objects: allObjects,
          scale,
        });

        if (clickedObject) {
          onConnectionSelect(clickedObject);
        }
        return; // Don't process other click handling in connection mode
      }

      // A PIN on a fixed cabinet: the click releases it — the run sizes and
      // places it again (`fixedCabinetMarker`). Tested against where the pins
      // were drawn, before ordinary selection, because the pin sits on top
      // of the cabinet it belongs to.
      if (onUnpinCabinet && fixedPinHitRef?.current?.length) {
        const pinned = fixedPinAt(fixedPinHitRef.current, {
          x: worldCoords.x,
          z: worldCoords.y,
        });
        if (pinned) {
          onUnpinCabinet(pinned);
          return;
        }
      }

      // Pick a NOTE. Tested before the wires and before ordinary selection
      // because the callout layer draws last and sits on top — what the user
      // clicked is whatever is painted where they clicked.
      if (onCalloutSelect && calloutHitRef?.current?.length) {
        const hit = calloutAtPoint(calloutHitRef.current, {
          x: mouseX,
          y: mouseY,
        });
        if (hit) {
          onCalloutSelect(hit);
          return;
        }
        // A click that misses every note clears the selection, the way
        // clicking empty plan clears an element.
        if (selectedCalloutId) onCalloutSelect(null);
      }

      // Pick a wiring RUN. Measured in screen pixels against the polyline the
      // overlay last drew (`wiringHitRef`), so the band is as wide as it looks
      // at any zoom and a wire that is not on screen is not clickable. Runs
      // are picked whole — nobody thinks in single hops.
      if (onWiringRunSelect && wiringHitRef?.current) {
        const {
          runs,
          screenPositions,
          visibleIds,
          scale: hitScale,
        } = wiringHitRef.current;
        const run = runAtPoint({
          runs,
          screenPositions,
          visibleIds,
          // The scale the overlay drew at: a hand-shaped wire is stored in
          // METRES, and without it the hit test measures the automatic curve
          // instead — a wire dragged off its old path would stop being
          // clickable along its new one.
          scale: hitScale,
          point: { x: mouseX, y: mouseY },
        });
        if (run) {
          onWiringRunSelect(run);
          return;
        }
        // A click that misses every wire clears the selection, the way
        // clicking empty plan clears an element.
        if (selectedWiringRunId) onWiringRunSelect(null);
      }

      // If in placement mode, place the object
      if (placementMode && onPlaceObject) {
        const defaultSize =
          placementMode.dimensions || getDefaultSize(placementMode.type);
        // Get the floor level for correct placement height
        const floorY = findFloorY(structureData);

        // Check if this is a door, window, opening, wall-light, mirror, or bathroom fixture that needs wall snapping
        const bathroomFixtureTypes = [
          'toilet',
          'sink',
          'bathtub',
          'shower',
          'vanity',
        ];
        const isBathroomFixture = bathroomFixtureTypes.includes(
          placementMode.type
        );
        // Kitchen/utility fixtures that need wall snapping
        const kitchenFixtureTypes = [
          'lower-cabinet',
          'upper-cabinet',
          'fireplace',
          'washerDryer',
          'dishwasher',
          'fridge',
          // Both old (stove / oven) and new (range / wall-oven) names.
          'oven',
          'wall-oven',
          'stove',
          'range',
        ];
        const isKitchenFixture = kitchenFixtureTypes.includes(
          placementMode.type
        );
        const needsWallSnap =
          placementMode.category?.door ||
          placementMode.category?.window ||
          placementMode.category?.opening ||
          placementMode.type === 'wall-light' ||
          placementMode.type === 'overhead-wall-light' ||
          placementMode.type === 'mirror' ||
          isWallElectricalDevice(placementMode.type) ||
          isKitchenFixture ||
          isBathroomFixture;

        // Debug logging
        if (
          placementMode.type === 'wall-light' ||
          placementMode.type === 'mirror' ||
          isBathroomFixture
        ) {
          console.log('Wall snap check for placement:', {
            type: placementMode.type,
            category: placementMode.category,
            needsWallSnap,
          });
        }

        // Collect room walls tagged with room center for interior-side-only snapping
        let allWalls = [];
        if (structureData?.rooms) {
          structureData.rooms.forEach((room) => {
            if (room.walls) {
              const validWalls = room.walls.filter((wall) => wall != null);

              // Compute room center from wall centers for interior-side filtering
              let sumX = 0,
                sumZ = 0,
                count = 0;
              validWalls.forEach((w) => {
                if (w.center) {
                  sumX += w.center.x;
                  sumZ += w.center.z;
                  count++;
                }
              });
              const roomCenter =
                count > 0 ? { x: sumX / count, z: sumZ / count } : null;

              // Tag each wall with its room id + center so snapping can pick
              // the host room's copy and enforce the interior side.
              const taggedWalls = validWalls.map((wall) => ({
                ...wall,
                _roomId: room.id,
                ...(roomCenter ? { _roomCenter: roomCenter } : {}),
              }));
              allWalls = allWalls.concat(taggedWalls);
            }
          });
        }

        // For doors/windows/openings, snap ONLY to structure walls
        const isOpeningType =
          placementMode.category?.door ||
          placementMode.category?.window ||
          placementMode.category?.opening;

        if (isOpeningType && structureWalls && structureWalls.length > 0) {
          allWalls = structureWalls
            .filter((w) => w != null)
            .map((w) => ({ ...w, _isStructureWallSnap: true }));
        }
        // Non-opening fixtures use room walls only, filtered to interior side by findClosestWall

        // A switch / outlet belongs to the room it was clicked in: a shared
        // wall's other copy faces the neighbour, and mounting on that one turns
        // the plan symbol into the wall (see wallsForHostRoom).
        const hostRoom = isWallElectricalDevice(placementMode.type)
          ? findRoomContainingPoint(
              worldCoords.x,
              worldCoords.y,
              structureData?.rooms || []
            )
          : null;
        if (isWallElectricalDevice(placementMode.type)) {
          allWalls = wallsForHostRoom(allWalls, hostRoom?.id);
        }

        if (needsWallSnap && allWalls.length > 0) {
          // Try to snap to nearest wall
          // Bathroom/kitchen fixtures use their type directly for snapping config lookup
          const elementType = placementMode.category?.door
            ? 'door'
            : placementMode.category?.window
              ? 'window'
              : placementMode.type === 'wall-light'
                ? 'wall-light'
                : placementMode.type === 'mirror'
                  ? 'mirror'
                  : isKitchenFixture ||
                      isBathroomFixture ||
                      isWallElectricalDevice(placementMode.type)
                    ? placementMode.type
                    : 'opening';

          const snapResult = snapElementToWall(
            { x: worldCoords.x, z: worldCoords.y },
            allWalls,
            defaultSize,
            elementType,
            floorY // Pass the actual floor Y position
          );

          if (!snapResult || !snapResult.wall) {
            // Don't allow placement if not on a wall. Say so: the preview's
            // red X is easy to read as the element itself, and a silent
            // refusal looked like the tool was broken.
            console.log(
              'Wall snap required but failed, preventing placement for:',
              {
                type: placementMode.type,
                elementType,
                snapResult,
              }
            );
            onNotification?.({
              message: `No wall here — move onto a wall to place the ${placementMode.label || 'element'}.`,
              type: 'warning',
            });
            return;
          }

          // Whole-wall demo: size the demoed opening to the clicked room's full
          // wall extent (vs Part Wall's fixed width). Everything else flows
          // through the normal demoed-opening placement path below, which writes
          // the structure copy + a room copy per overlapping room.
          let wholeWallSpan = null;
          if (placementMode.type === 'wholeWall') {
            wholeWallSpan = computeClickedRoomWallSpan(
              { wall: snapResult.wall, position: snapResult.position },
              structureData?.rooms || []
            );
            if (!wholeWallSpan) {
              console.log(
                'Whole-wall: no room-wall span at click; skipping placement.'
              );
              return;
            }
          }

          // Find which room owns this wall (skip for structure wall snaps)
          const isStructureWallSnap = !!snapResult.wall._isStructureWallSnap;
          let parentRoomId = null;
          if (!isStructureWallSnap && structureData?.rooms) {
            for (const room of structureData.rooms) {
              if (room.walls) {
                const hasWall = room.walls.some((wall) => {
                  // Check direct reference equality
                  if (wall === snapResult.wall) return true;

                  // Check by id
                  if (
                    wall.id &&
                    snapResult.wall.id &&
                    wall.id === snapResult.wall.id
                  )
                    return true;

                  // Check by center position (as fallback)
                  if (
                    wall.center &&
                    snapResult.wall.center &&
                    Math.abs(wall.center.x - snapResult.wall.center.x) < 0.01 &&
                    Math.abs(wall.center.z - snapResult.wall.center.z) < 0.01
                  ) {
                    return true;
                  }

                  return false;
                });
                if (hasWall) {
                  parentRoomId = room.id;
                  break;
                }
              }
            }
          }

          // Get wall thickness - walls have dimensions where z is thickness
          // Use the actual thickness, even if it's 0. Only use default if dimensions don't exist
          const wallThickness =
            snapResult.wall.dimensions?.z !== undefined
              ? snapResult.wall.dimensions.z
              : 0.1; // Default to 10cm only if dimensions are missing

          // Keep zero thickness as zero - don't add minimum

          // For wall-lights, mirrors, bathroom fixtures, and kitchen fixtures, keep their original dimensions
          // For doors/windows/openings, match wall thickness.
          // For demoed walls, also match wall HEIGHT (see comment in the
          // preview block above — placement default is 8' but demoed
          // sections should fill the parent wall).
          const adjustedDimensions =
            elementType === 'wall-light' ||
            elementType === 'mirror' ||
            isWallElectricalDevice(placementMode.type) ||
            isKitchenFixture ||
            isBathroomFixture
              ? defaultSize // Wall lights, mirrors, bathroom fixtures, and kitchen fixtures keep their own dimensions
              : {
                  ...defaultSize,
                  z: wallThickness, // Doors/windows/openings match wall thickness
                  ...(isDemoedOpening(placementMode) &&
                  snapResult.wall.dimensions?.y
                    ? { y: snapResult.wall.dimensions.y }
                    : {}),
                  // Whole-wall: override x to the clicked room's full wall extent.
                  ...(wholeWallSpan ? { x: wholeWallSpan.dimensions.x } : {}),
                };

          // For demoed walls, vertically center on the parent wall.
          // Whole-wall uses the span midpoint as the along-wall center.
          const adjustedCenter = wholeWallSpan
            ? {
                x: wholeWallSpan.center.x,
                y: snapResult.wall.center?.y ?? snapResult.position.y,
                z: wholeWallSpan.center.z,
              }
            : isDemoedOpening(placementMode) &&
                snapResult.wall.center?.y !== undefined
              ? { ...snapResult.position, y: snapResult.wall.center.y }
              : snapResult.position;

          // A wall device faces the room, decided by a polygon probe: the
          // snapper picks the side from the room's average center, which sits
          // on the wrong side of its own wall in an L-shaped room.
          const placedRotation = isWallElectricalDevice(placementMode.type)
            ? {
                ...snapResult.rotation,
                y: faceWallDeviceIntoRoom(
                  snapResult.rotation?.y || 0,
                  adjustedCenter,
                  roomForSnappedWall(snapResult.wall, structureData?.rooms)
                ),
              }
            : snapResult.rotation;

          // Create the element snapped to the wall
          const newObject = {
            id: `${placementMode.type}_${Date.now()}`,
            type: placementMode.type,
            label: placementMode.label,
            category: placementMode.category,
            center: adjustedCenter,
            dimensions: adjustedDimensions,
            rotation: placedRotation,
            parentWallIds: [snapResult.wall.id], // Parent wall identifier array
            parentRoomId: parentRoomId, // Include the room that owns the wall
            ...(placementMode.wallCount != null && {
              wallCount: placementMode.wallCount,
            }),
            ...(isStructureWallSnap && { _isStructureWallSnap: true }),
          };

          // Debug wall-light parentWallIds
          if (elementType === 'wall-light') {
            console.log('Creating wall-light with:', {
              wallId: snapResult.wall.id,
              wall: snapResult.wall,
              newObject,
            });
          }

          onPlaceObject(newObject);
          // Keep the element selected after placement
          setInternalSelectedItem(newObject);
        } else if (isSink({ type: placementMode.type })) {
          // Special handling for sink placement
          // Sinks must be placed on existing countertops

          // Import validation utilities at the top of the file would be ideal,
          // but for now we'll inline the validation logic

          // Find countertop at click position
          const countertops = [];

          if (structureData?.rooms) {
            structureData.rooms.forEach((room) => {
              if (room.objects) {
                room.objects.forEach((obj) => {
                  if (obj.type === 'countertop' || obj.category?.countertop) {
                    countertops.push({
                      ...obj,
                      roomId: room.id,
                      sinks: obj.sinks || [], // Existing sinks on this countertop
                    });
                  }
                });
              }
            });
          }

          // Find countertop at this position. Rotation-aware: a slab's stored
          // dimensions are in its OWN frame, so testing them against world
          // axes checks a box turned by the slab's rotation.
          const targetCountertop = countertopAtPoint(
            worldCoords.x,
            worldCoords.y,
            countertops
          );

          if (!targetCountertop) {
            console.log('❌ No countertop found at click position');
            console.log('Available countertops:', countertops.length);
            console.log('Click coords:', worldCoords);
            if (onNotification) {
              onNotification({
                message:
                  'Counter-mounted sinks can only be placed on countertops',
                type: 'error',
              });
            }
            return;
          }

          console.log('✅ Found countertop:', targetCountertop);

          // Get existing sinks on this countertop (from both storage locations)
          const countertopId = targetCountertop.id;

          // Need to find the room containing this countertop for getSinksOnCountertop
          let roomWithCountertop = null;
          if (structureData?.rooms) {
            roomWithCountertop = structureData.rooms.find((room) =>
              room.objects?.some(
                (obj) =>
                  obj.id === countertopId &&
                  (obj.type === 'countertop' || obj.category?.countertop)
              )
            );
          }

          const existingSinks = roomWithCountertop
            ? getSinksOnCountertop(countertopId, roomWithCountertop)
            : [];

          // Calculate sink position with 3.5" snap from front edge BEFORE validation
          const FRONT_EDGE_OFFSET = 0.0889; // 3.5 inches in meters
          const SINK_DEPTH = 0.457; // 18 inches standard sink depth in meters

          // Get countertop dimensions
          const ctDims = Array.isArray(targetCountertop.dimensions)
            ? {
                x: targetCountertop.dimensions[0],
                y: targetCountertop.dimensions[1],
                z: targetCountertop.dimensions[2],
              }
            : targetCountertop.dimensions;

          // Determine front edge based on countertop rotation
          // Cabinet model space: +Z is front (doors), -Z is back (wall-facing)
          // rotation.y determines which world direction the cabinet faces
          const rotation = targetCountertop.rotation || { x: 0, y: 0, z: 0 };
          const rotationY = rotation.y || 0;

          // Calculate sink CENTER position such that sink FRONT EDGE is 3.5" inward
          let sinkX = worldCoords.x;
          let sinkZ = worldCoords.y;

          // Normalize rotation to 0-360 degrees
          const normalizedRotation = ((rotationY % 360) + 360) % 360;

          if (normalizedRotation >= 315 || normalizedRotation < 45) {
            // rotation.y ≈ 0°: Cabinet faces +Z, front edge at max Z, back at min Z
            // Sink front edge should be 3.5" inward from cabinet front edge
            // Sink center = (cabinet front edge - 3.5") - (sink depth / 2)
            const cabinetFrontEdgeZ = targetCountertop.center.z + ctDims.z / 2;
            const sinkFrontEdgeZ = cabinetFrontEdgeZ - FRONT_EDGE_OFFSET;
            sinkZ = sinkFrontEdgeZ - SINK_DEPTH / 2;
          } else if (normalizedRotation >= 45 && normalizedRotation < 135) {
            // rotation.y ≈ 90°: Cabinet faces -X, front edge at min X, back at max X
            // Sink center = (cabinet front edge + 3.5") + (sink depth / 2)
            const cabinetFrontEdgeX = targetCountertop.center.x - ctDims.x / 2;
            const sinkFrontEdgeX = cabinetFrontEdgeX + FRONT_EDGE_OFFSET;
            sinkX = sinkFrontEdgeX + SINK_DEPTH / 2;
          } else if (normalizedRotation >= 135 && normalizedRotation < 225) {
            // rotation.y ≈ 180°: Cabinet faces -Z, front edge at min Z, back at max Z
            // Sink center = (cabinet front edge + 3.5") + (sink depth / 2)
            const cabinetFrontEdgeZ = targetCountertop.center.z - ctDims.z / 2;
            const sinkFrontEdgeZ = cabinetFrontEdgeZ + FRONT_EDGE_OFFSET;
            sinkZ = sinkFrontEdgeZ + SINK_DEPTH / 2;
          } else {
            // rotation.y ≈ 270°: Cabinet faces +X, front edge at max X, back at min X
            // Sink center = (cabinet front edge - 3.5") - (sink depth / 2)
            const cabinetFrontEdgeX = targetCountertop.center.x + ctDims.x / 2;
            const sinkFrontEdgeX = cabinetFrontEdgeX - FRONT_EDGE_OFFSET;
            sinkX = sinkFrontEdgeX - SINK_DEPTH / 2;
          }

          // Validate sink placement using helper with SNAPPED position
          const validation = validateSinkPlacement(
            { x: sinkX, z: sinkZ },
            targetCountertop,
            existingSinks
          );

          if (!validation.valid) {
            console.log('❌ Validation failed:', validation.message);
            if (onNotification) {
              onNotification({
                message: validation.message,
                type: 'error',
              });
            }
            return;
          }

          console.log('✅ Validation passed');

          console.log('🎯 Sink snapping:', {
            originalClick: { x: worldCoords.x, z: worldCoords.y },
            snappedPosition: { x: sinkX, z: sinkZ },
            countertopRotation: rotationY,
            frontEdgeOffset: FRONT_EDGE_OFFSET,
          });

          // Create sink object with parents array (NEW SYSTEM)
          const newSink = createSinkObject(
            getSinkType({ type: placementMode.type }),
            {
              x: sinkX,
              y: targetCountertop.center.y, // Use countertop Y position
              z: sinkZ,
            },
            countertopId
          );

          // Add room context
          newSink.roomId = targetCountertop.roomId;
          newSink._roomId = targetCountertop.roomId;

          console.log('🚰 Created sink object:', {
            sinkId: newSink.id,
            countertopId: countertopId,
            parents: newSink.parents,
            center: newSink.center,
            roomId: newSink.roomId,
            fullSink: newSink,
          });

          // Place sink as a regular object (NOT update-countertop-sinks)
          onPlaceObject(newSink);

          console.log('✅ Sink placed successfully (new parents array system)');

          if (onNotification) {
            onNotification({
              message: `Sink ${existingSinks.length + 1} of 2 added to countertop`,
              type: 'success',
            });
          }

          return; // Don't process normal click selection
        } else if (placementMode.type === 'bathroom-sink-faucet') {
          // Special handling for faucet placement (async for model fetch)
          (async () => {
            // Faucets must be placed on existing sinks on countertops

            // Find all countertops and their sinks (from both storage locations)
            const countertopsWithSinks = [];

            if (structureData?.rooms) {
              structureData.rooms.forEach((room) => {
                if (room.objects) {
                  room.objects.forEach((obj) => {
                    if (obj.type === 'countertop' || obj.category?.countertop) {
                      const countertopId = obj.id;
                      const sinks = getSinksOnCountertop(countertopId, room);

                      if (sinks.length > 0) {
                        countertopsWithSinks.push({
                          countertop: obj,
                          sinks: sinks,
                          roomId: room.id,
                        });
                      }
                    }
                  });
                }
              });
            }

            if (countertopsWithSinks.length === 0) {
              console.log('❌ No countertops with sinks found');
              if (onNotification) {
                onNotification({
                  message: 'Faucets can only be placed on sinks',
                  type: 'error',
                });
              }
              return;
            }

            // Find the closest sink to the click position
            let closestSink = null;
            let closestCountertop = null;
            let closestRoomId = null;
            let minDistance = Infinity;

            for (const item of countertopsWithSinks) {
              for (const sink of item.sinks) {
                // Get sink position - handle both center and position fields
                const sinkPos = sink.center || sink.position;
                if (!sinkPos) continue;

                const dx = worldCoords.x - sinkPos.x;
                const dz = worldCoords.y - sinkPos.z;
                const distance = Math.sqrt(dx * dx + dz * dz);

                if (distance < minDistance) {
                  minDistance = distance;
                  closestSink = sink;
                  closestCountertop = item.countertop;
                  closestRoomId = item.roomId;
                }
              }
            }

            if (!closestSink || !closestCountertop) {
              console.log('❌ No sink found near click position');
              if (onNotification) {
                onNotification({
                  message: 'Please click near a sink to place a faucet',
                  type: 'error',
                });
              }
              return;
            }

            // Ensure faucet model data is loaded — the async fetch in
            // handleAddElement may not have completed by the time the user clicks.
            // getDefaultGLBModel uses a cache, so subsequent calls are instant.
            const faucetGlbData =
              placementMode.glbModelData ||
              (await getDefaultGLBModel('bathroom-sink-faucet'));

            // Use shared createSinkFaucet for positioning (same as design tool)
            const sinkPos = closestSink.center || closestSink.position;
            const countertopDimensions = closestCountertop.dimensions || {
              y: 0.038,
            };
            const countertopCenterY = closestCountertop.center?.y || 0;
            const countertopSurfaceY =
              countertopCenterY + countertopDimensions.y / 2;

            const sinkRotation = closestSink.rotation ||
              closestCountertop.rotation || { x: 0, y: 0, z: 0 };

            // Build sink model data for setback calculation
            const sinkModelData = {};
            if (closestSink['sink-cutout']) {
              sinkModelData['sink-cutout'] = closestSink['sink-cutout'];
            }
            if (closestSink.modelDimensions) {
              sinkModelData.dimensions = closestSink.modelDimensions;
            }

            // Find parent wall for wall-mount support
            const parentWallIds = closestCountertop.parentWallIds;
            let roomWalls = [];
            if (structureData?.rooms) {
              const room = structureData.rooms.find(
                (r) => r.id === closestRoomId
              );
              if (room) roomWalls = room.walls || [];
            }

            // Normalize model data URL field — unifiedObjects uses glbURL,
            // but createSinkFaucet checks faucetModelData.url
            const faucetModelData = faucetGlbData
              ? {
                  ...faucetGlbData,
                  url:
                    faucetGlbData.url ||
                    faucetGlbData.glbURL ||
                    faucetGlbData.downloadURL ||
                    faucetGlbData.modelUrl,
                }
              : null;

            // Contain the faucet on the counter, the same bound the assembly
            // builders pass. The setback is a function of the SINK alone, so
            // dropping a faucet onto a shallow vanity puts the body in the
            // wall without it.
            const faucetGroupId = closestCountertop.fixtureGroupId;
            const groupCabinet = faucetGroupId
              ? (
                  structureData?.rooms?.find((r) => r.id === closestRoomId)
                    ?.objects || []
                ).find(
                  (o) =>
                    o.fixtureGroupId === faucetGroupId &&
                    (isLowerCabinetLike(o) || o.category?.cabinet)
                )
              : null;

            const newFaucet = createSinkFaucet({
              sinkPos: { x: sinkPos.x, z: sinkPos.z },
              sinkId: closestSink.id,
              sinkType: getSinkType(closestSink) || 'undermount',
              countertopId: closestCountertop.id,
              countertopSurfaceY,
              countertop:
                resolveCounterContainer(closestCountertop, groupCabinet) ||
                undefined,
              rotation: sinkRotation,
              faucetModelData,
              sinkModelData:
                Object.keys(sinkModelData).length > 0 ? sinkModelData : null,
              fixtureGroupId: closestCountertop.fixtureGroupId || '',
              faucetId: `bathroom-sink-faucet_${Date.now()}`,
              label: 'Faucet',
              addedBy: 'floor-plan-placement',
              // `faucetGlbData`, not `placementMode.glbModelData`: the latter is
              // empty whenever handleAddElement's async fetch has not landed by
              // click time, and reading it directly silently mounts a wall-mount
              // faucet on the counter. That is the fallback three lines above
              // this block's sibling, applied to the one field that skipped it.
              mountType:
                faucetGlbData?.mountType === 'wall-mount' ? 'wall' : 'counter',
              parentWallIds,
              walls: roomWalls,
              vanityDepth:
                closestCountertop.dimensions?.z ||
                closestCountertop.dimensions?.depth,
            });

            // Add fields expected by the floor plan placement system
            newFaucet.roomId = closestRoomId;
            newFaucet._roomId = closestRoomId;
            newFaucet.position = newFaucet.center;
            newFaucet.placedAt = new Date().toISOString();

            // Notify parent to add the faucet as a regular object
            onPlaceObject(newFaucet);

            console.log(
              '✅ Faucet placed successfully on sink:',
              closestSink.id
            );

            if (onNotification) {
              onNotification({
                message: 'Faucet added to sink',
                type: 'success',
              });
            }
          })();
          return; // Don't process normal click selection
        } else {
          // Regular object placement (not door/window/opening/wall/sinks/faucets)

          // Use preview object dimensions if available (for smart-sized objects like countertops)
          // Otherwise use default size
          const objectDimensions = previewObject?.dimensions || defaultSize;
          const targetCabinet = previewObject?.targetCabinet || null;

          // The horizontal surface a surface device landed on, if any. Used
          // for BOTH its height and its rotation, so the two cannot describe
          // different slabs.
          const supportSurface = isSurfaceElectricalDevice(placementMode.type)
            ? supportSurfaceAt(
                worldCoords.x,
                worldCoords.y,
                collectAllObjects(structureData)
              )
            : null;

          // Calculate Y position based on object type
          let yPosition;
          if (
            isCeilingElectricalDevice(placementMode.type) ||
            placementMode.proceduralStyle === 'recessed'
          ) {
            // Seated against the ceiling: body midpoint half its height below.
            const ceilingHeight = getCeilingHeightAtPoint(
              worldCoords.x,
              worldCoords.y,
              structureData?.rooms
            );
            yPosition = floorY + ceilingHeight - objectDimensions.y / 2;
          } else if (placementMode.type === 'ceiling-light') {
            // Ceiling lights should be placed at 8 feet (2.44 meters) from the floor
            // Position the bottom of the light at 8 feet
            yPosition = floorY + 2.44;
          } else if (isSurfaceElectricalDevice(placementMode.type)) {
            // A surface outlet sits on whatever horizontal surface it was
            // dropped on — the countertop under the click, else the floor.
            // Slab tops are world-absolute, so they replace floorY rather than
            // add to it.
            yPosition =
              (supportSurface ? supportSurface.topY : floorY) +
              objectDimensions.y / 2;
          } else {
            // Other objects are placed on the floor
            yPosition = floorY + objectDimensions.y / 2;
          }

          const newObject = {
            id: `${placementMode.type}_${Date.now()}`,
            type: placementMode.type,
            label: placementMode.label,
            category: placementMode.category, // Include category for regular objects too
            center: {
              x: worldCoords.x,
              y: yPosition,
              z: worldCoords.y, // COORDINATE MAPPING: Canvas Y → 3D Z
            },
            dimensions: objectDimensions, // Use preview dimensions if available
            // A device set into a countertop runs square to it — that is how
            // a pop-up is installed. Only visible in 3D (the plan symbol is a
            // circle). On the floor there is nothing to square up to, so the
            // preview's own rotation stands.
            rotation: {
              x: 0,
              y: supportSurface
                ? supportSurface.surface.rotation?.y || 0
                : previewRotation,
              z: 0,
            },
            targetCabinet: targetCabinet, // Pass target cabinet info for smart positioning
            ...(placementMode.proceduralStyle && {
              proceduralStyle: placementMode.proceduralStyle,
            }),
          };

          onPlaceObject(newObject);
        }
        return; // Don't process normal click selection
      }

      // Element detection uses the SAME filtered data the render path uses,
      // so single-room mode only hit-tests visible elements — including the
      // structure walls, which are narrowed to the ones backing this room's
      // walls (an invisible wall from another room used to be selectable
      // from a blank spot on screen).
      const dataToRender = getDataToRender(structureData);
      const wallsForHitTest = selectedRoom
        ? structureWallsForRoomWalls(
            structureWalls,
            (dataToRender?.rooms || []).flatMap((r) => r.walls || [])
          )
        : structureWalls;
      const detected = detectClickedElement(
        worldCoords.x,
        worldCoords.y,
        dataToRender,
        showRoomLabels,
        wallsForHitTest
      );

      // Dimension labels are drawn over everything, so they take the click
      // over walls and empty floor — but NOT over an object under them: a
      // chair sitting beneath a wall dimension must still be selectable.
      const primary = detected.primary;
      const objectUnderClick =
        !!primary &&
        !primary.isWall &&
        primary.itemType !== 'door' &&
        !primary.isWindow &&
        !primary.isOpening &&
        primary.itemType !== 'roomLabel' &&
        !primary.isRoomLabel;
      const hitMeasurement = objectUnderClick
        ? null
        : hitTestMeasurement(
            worldCoords.x,
            worldCoords.y,
            measurementsRef.current
          );

      if (hitMeasurement) {
        // A measurement was clicked — select it and clear the local
        // element selection. We deliberately do NOT call onItemSelect
        // (null) here: the parent's handleItemSelect treats null as
        // "clicked empty space" and clears selectedRoom + roomId URL
        // param, which switches the canvas from per-room measurements
        // (T0 + T0.5 + T1 + T2) to whole-structure wall-only
        // measurements — so the measurement the user just clicked
        // literally vanishes from the visible set. The parent clears
        // its own element state via onMeasurementSelect instead, which
        // preserves the room context.
        setSelectedFloorPlanMeasurement(hitMeasurement);
        if (onMeasurementSelect) {
          onMeasurementSelect(hitMeasurement);
        }
        setInternalSelectedItem(null);
        setWorldX(worldCoords.x);
        setWorldY(worldCoords.y);
        return;
      }

      const allElements = detected.all;
      let elementToSelect = detected.primary;

      // Click-to-cycle: if clicking the same spot with multiple items,
      // cycle through them instead of always selecting the primary
      if (elementToSelect && allElements && allElements.length > 1) {
        const cycle = overlapCycleRef.current;
        // Check if the same set of overlapping items is present
        const sameSet =
          cycle.items.length === allElements.length &&
          cycle.items.every((prev) =>
            allElements.some((cur) => cur.id === prev.id)
          );

        if (sameSet && selectedItem?.id) {
          // Same overlapping set — advance to next item
          const nextIndex = (cycle.index + 1) % allElements.length;
          elementToSelect = allElements[nextIndex];
          overlapCycleRef.current = {
            items: allElements,
            index: nextIndex,
          };
        } else {
          // New set of overlapping items — start fresh with primary
          overlapCycleRef.current = { items: allElements, index: 0 };
        }
      } else {
        // Single item or nothing — reset cycle
        overlapCycleRef.current = { items: [], index: 0 };
      }

      // Clear measurement selection when clicking elsewhere
      setSelectedFloorPlanMeasurement(null);
      if (onMeasurementSelect) {
        onMeasurementSelect(null);
      }

      setInternalSelectedItem(elementToSelect);

      // Calculate if we need to shift the canvas
      if (elementToSelect && canvasRef.current) {
        const PANEL_WIDTH = 400; // Panel width (380px) + some margin
        const MARGIN = 50; // Extra margin to ensure object is clearly visible

        // Get object center in world coordinates
        let objectWorldX, objectWorldY;

        if (elementToSelect.center) {
          objectWorldX = elementToSelect.center.x;
          objectWorldY = elementToSelect.center.z;
        } else if (
          elementToSelect.isWall &&
          elementToSelect.start &&
          elementToSelect.end
        ) {
          objectWorldX = (elementToSelect.start.x + elementToSelect.end.x) / 2;
          objectWorldY = (elementToSelect.start.z + elementToSelect.end.z) / 2;
        } else if (
          elementToSelect.sections &&
          elementToSelect.sections.length > 0
        ) {
          const section = elementToSelect.sections[0];
          if (section.center) {
            objectWorldX =
              section.center.x !== undefined
                ? section.center.x
                : section.center[0];
            objectWorldY =
              section.center.z !== undefined
                ? section.center.z
                : section.center[2];
          } else {
            objectWorldX = worldCoords.x;
            objectWorldY = worldCoords.y;
          }
        } else {
          objectWorldX = worldCoords.x;
          objectWorldY = worldCoords.y;
        }

        const screenPos = worldToScreenCoordinates(
          objectWorldX,
          objectWorldY,
          canvasRef.current,
          pan,
          scale,
          rotation
        );

        if (screenPos.x < PANEL_WIDTH + MARGIN) {
          if (!autoPanState.hasAutoPanned) {
            setAutoPanState({
              hasAutoPanned: true,
              originalPan: { ...pan },
            });
          }

          const shiftAmount = PANEL_WIDTH + MARGIN - screenPos.x + 50;
          const startPan = { ...pan };
          const targetPan = { x: pan.x + shiftAmount, y: pan.y };
          const animationDuration = 300;
          const startTime = performance.now();

          const animatePan = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / animationDuration, 1);
            const easeOut = 1 - Math.pow(1 - progress, 3);
            const newPanX = startPan.x + (targetPan.x - startPan.x) * easeOut;
            const newPan = { x: newPanX, y: startPan.y };
            setPan(newPan);
            if (onPanChange) {
              onPanChange(newPan);
            }
            if (progress < 1) {
              requestAnimationFrame(animatePan);
            }
          };

          requestAnimationFrame(animatePan);
        }
      }

      if (onItemSelect) {
        // Strip transient drag/move/resize flags — this is a click selection,
        // not a drag completion. Without this, handleItemSelect would treat
        // the item as a moved object and re-save to Firebase.
        const cleanItem = elementToSelect
          ? {
              ...elementToSelect,
              isMoved: false,
              isDragging: false,
              isResized: false,
              isWallDragged: false,
              isWallEdited: undefined,
            }
          : elementToSelect;

        onItemSelect(cleanItem, allElements);
      }

      // Update debug state
      setWorldX(worldCoords.x);
      setWorldY(worldCoords.y);
    },
    [
      structureData,
      pan,
      scale,
      rotation,
      onItemSelect,
      autoPanState,
      showRoomLabels,
      hasDraggedMouse,
      placementMode,
      onPlaceObject,
      previewRotation,
      recessedRowDrawing,
      wallChainDrawing,
      connectionMode,
      onConnectionSelect,
      onWiringRunSelect,
      selectedWiringRunId,
      wiringHitRef,
      calloutDrawing,
      calloutHitRef,
      fixedPinHitRef,
      onUnpinCabinet,
      onCalloutSelect,
      selectedCalloutId,
      onMeasurementSelect,
      selectedItem,
    ]
  );
  // === END VERBATIM ===

  return {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleClick,
    containerWheelRef,
  };
}
